/**
 * Die öffentliche Berichtsseite zu einer Studie: /bericht?t=<share_token>
 *
 * WARUM SIE HIERHER UMGEZOGEN IST: Sie lag in `Blessing-Digital/99takes-website`
 * und war dort **kaputt** — 15 Bytes, `[object Object]`, weil der
 * Cloudflare-Adapter der Website serverseitige Seiten nicht mehr rendert.
 * `SIM_REPORT_PUBLIC_URL` zeigt dorthin, also endete jeder Link aus
 * `publish_report` auf einer toten Seite. Ausgerechnet das Stück, das eine
 * Agentur ihrem Kunden schickt.
 *
 * WAS DER UMZUG KOSTET: Die alte Fassung war serverseitig gerendert und
 * funktionierte ohne JavaScript. Das ist hier weg. Der Tausch lohnt trotzdem:
 * eine Seite, die ohne JavaScript funktioniert hätte, es aber mit und ohne
 * nicht tut, hat diesen Vorteil nicht. Drucken geht weiterhin — das
 * Druck-CSS steht unten.
 *
 * KEIN APP-RAHMEN. Das hier ist ein Dokument, kein Seitenbesuch. Wer es
 * bekommt, prüft eine Entscheidung; Navigation und Abmelden-Knopf stünden im
 * Weg. Eigene Typografie, eigenes Papier.
 *
 * AUFBAU SEIT 07.10.2026 (Robert: „das Design des Reports ist noch
 * unübersichtlich"; Entwurf „99takes Report-Template" in Claude Design, Vorbild
 * der Kundenbericht für Weiterbildung Now im sea-agency-playbook). Vorher stand
 * jede Fassung mit beiden Volltexten untereinander, die Vergleichsfassung also
 * einmal je Run (Magnolia: 52.000 px), und das Urteil oben galt nur dem
 * letzten Run. Jetzt:
 *
 *   01 Auf einen Blick: drei Kernaussagen (vom Autor), Empfehlung, Verlauf
 *   02 Persona für Persona: Abstand je Persona in jeder Fassung
 *   03 Was zieht (Autor), was bremst (gebündelte Einwände)
 *   (seit dem zweiten Stand direkt nach 01: die Texte zum Aufklappen, mit
 *   Leseansicht neben der Vergleichsfassung)
 *   04 Wer skeptisch bleibt, 05 Empfehlungen, 07 wer urteilt
 *
 * SEIT 09.10.2026: EIN BLATT, DANN AUFKLAPPEN (Variante A aus dem Canvas
 * „99takes Report-Template“, Robert). Drei Ebenen: oben Urteil, Umfang, drei
 * Kennzahlen und was zu tun ist; darunter die Befunde je Abschnitt der Seite
 * (bericht-blatt.ts); unten alles Weitere als Akkordeon, im Druck offen.
 * Nichts von oben ist weggefallen, es steht jetzt in den Akkordeons.
 *
 * SEIT 12.10.2026: DER KOPF ERKLÄRT DEN TEST (Entwurf „Final“ im Canvas
 * „99takes Bericht verständlich“, Robert). Die Überschrift sagt, was getestet
 * wurde und bei wem, drei Kästen sagen, was gegen was antrat, wer urteilte und
 * was herauskam, die Zeitleiste „So sind wir vorgegangen“ führt in Wortstufen
 * zum Ergebnis (bericht-vorgehen.ts). Die drei Kennzahl-Karten sind weg, ihre
 * Zahlen stehen im Aufklapper „Wie die Stufen entstehen“.
 *
 * SEIT 09.10.2026 AUCH ALS VORSCHLAG (Layout S3 „Beweiskette“, Robert): trägt
 * der Snapshot `vorschlag`, zeigt BerichtVorschlag.tsx erst die neue Fassung,
 * dann die Belege (Text-Simulation, Design-Simulation, Fachprüfer), und darunter
 * dieselben Akkordeons wie hier. Ohne `vorschlag` bleibt alles wie oben.
 *
 * Übernommen wird nur, was die Nutzlast aus `report.ts` trägt. Jobs, Profile
 * und Reaktionstexte stehen dort bewusst nicht drin (bestätigt am 07.10.2026),
 * also auch hier nicht. Was nur aus ihnen käme, schreibt der Autor über
 * publish_report (`narrative.kernpunkte`, `hebel`, `empfehlungen`); fehlen die
 * Felder (alle Berichte vor dem 07.10.2026), steht der alte Einstieg da.
 */

import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router';
import { SUPABASE_URL } from '../config';
import { LeserAnmeldung, leseSitzung, vergissSitzung } from '../bericht-zugang';
import { ApiFehler, ladeEigenenBericht } from '../api';
import { sichererLink } from '../sicherer-link';
import { anzahlUrteile, personaNoten } from '../bericht-noten';
import { NotenJePersona } from '../noten-je-persona';
import { BesetztListe, besetztTitel } from '../besetzt-liste';
import { kollidiert, kollisionenDesBerichts, KollisionsHinweis, LeitplankenListe, leitplankenTitel, personenMitKollision } from '../leitplanken-liste';
import { ErsterBildschirm, ersterBildschirmTitel } from '../erster-bildschirm';
import { beleglageAusPersonas, beleglageZeile } from '../beleglage';
import { befundeDerFassung, befundZahl, schrittSatz } from '../befunde';
import { bilanzSatz, leseBilanz, leseKernRand, leseKernRandWeiter, leseMuster, musterSatz, randWarnung, randZeile, randZeileWeiter } from '../kern-rand';
import { fassungsname } from '../fassungsname';
import { berichtsZeilen, type BerichtZeile, type Teil } from '../fassungen-abgleich';
import {
  befundeJeAbschnitt, ersterSatz, dreiZuTun, gepruefteSeite, kernRandZahl, KRITERIUM, nennerSatz, schrittJa, standDer,
  umfangKurz, urteilDesBerichts, type AbschnittsZeile, type Art, type GepruefteSeite,
} from '../bericht-blatt';
import {
  aufzaehlung, ausgangslage, designSchritt, ergebnisReihe, ersterBildschirmSchritt, fassungenEinleitung, fassungsKarten, pruefungen, geurteiltSatz, getestetSatz, leseLink, naechsteRunde, schrittFolge,
  titelDesBerichts, vergleichImSatz, VON_ETIKETT, zielgruppe, type DesignSchritt, type ErsterBildschirmSchritt, type FassungsKarte, type NaechsteRunde, type Pruefung, type SchrittArt,
} from '../bericht-vorgehen';
import {
  ablauf, designEintraege, fachprueferText, kernRandText, umfangZeile, fassungenNachHash, gradText, lesbaresSegment, notenText, standDesBerichts, teileBefunde,
  vergleicheBefunde, vergleicheNoten, type BefundZeile, type FassungRef, type Schritt,
} from '../bericht-ablauf';
import {
  abschnitte, buendleEinwaende, fassungsMatrix, kurzname, skeptiker, stufe, vergleicheAbschnitte, zeichenZeile,
  type Aenderung, type Matrix, type Stufe,
} from '../bericht-auswertung';
import { designImDetail, leseVorschlag, vergleichFuerSatz } from '../bericht-vorschlag';
import { testlaufSatz } from '../bericht-testlauf';
import { BerichtVorschlag } from './BerichtVorschlag';
import { locale, sprache, t } from '../sprache';
import { SprachWahl } from '../sprache-wahl';

const URTEIL: Record<string, string> = {
  candidate: 'gewinnt',
  baseline: 'verliert',
  tie: 'unentschieden',
};
const URTEIL_EN: Record<string, string> = {
  candidate: 'wins',
  baseline: 'loses',
  tie: 'tie',
};
const urteilWort = (v: string): string | undefined => t(URTEIL, URTEIL_EN)[v];

const tag = (iso: unknown): string => {
  const d = new Date(String(iso ?? ''));
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString(locale(), { day: '2-digit', month: '2-digit', year: 'numeric' });
};

type Lage = 'laedt' | 'da' | 'ohne-token' | 'nicht-gefunden' | 'anmelden' | 'geschuetzt';

export default function Bericht() {
  const [params] = useSearchParams();
  const token = params.get('t') ?? '';
  // ?id=… ist die Ansicht des Eigentuemers: derselbe Bericht, aus dem Konto
  // geladen statt ueber den Link. Seit dem 25.09.2026 — vorher war ein Bericht
  // ohne Link nirgends einsehbar, auch nicht fuer den, der ihn erzeugt hat.
  const eigeneId = params.get('id') ?? '';
  const [lage, setLage] = useState<Lage>('laedt');
  const [data, setData] = useState<any>(null);
  // Hochgezaehlt nach der Anmeldung eines Lesers: laedt den Bericht neu.
  const [angemeldet, setAngemeldet] = useState(0);
  // Welche Fassung eine Skizze zeigt: Hash des Texts wie auf dem Server (bericht-ablauf.ts).
  const [fassungen, setFassungen] = useState<Record<string, FassungRef>>({});
  useEffect(() => {
    // Skizzen stehen in den Checks der Studie oder, im Bericht als Vorschlag, als neue Fassung im Vergleich.
    const skizzen = [...(Array.isArray(data?.design) ? data.design : []), data?.design_vergleich?.neu].filter((d: any) => d?.skizze);
    if (!Array.isArray(data?.variants) || !skizzen.length) return;
    let lebt = true;
    fassungenNachHash(data.variants).then((f) => { if (lebt) setFassungen(f); }, () => {});
    return () => { lebt = false; };
  }, [data]);

  useEffect(() => {
    if (token || !eigeneId) return;
    setLage('laedt');
    setData(null);
    let lebt = true;
    ladeEigenenBericht(eigeneId).then(
      d => { if (lebt) { setData(d); setLage('da'); } },
      (e: unknown) => { if (lebt) setLage(e instanceof ApiFehler && e.abgemeldet ? 'anmelden' : 'nicht-gefunden'); },
    );
    return () => { lebt = false; };
  }, [token, eigeneId]);

  useEffect(() => {
    if (!token && eigeneId) return; // die Eigentuemer-Ansicht laedt oben
    // Der Zustand wird HIER abgeleitet und nicht im useState-Initialisierer:
    // der laeuft nur beim ersten Rendern. Wechselt die Kennung in der Adresse,
    // bliebe sonst die Meldung des vorigen Berichts stehen — beim Testen zeigte
    // ?t=<ungueltig> die Meldung "Kennung fehlt".
    if (!token) { setLage('ohne-token'); setData(null); return; }
    setLage('laedt');
    setData(null);
    let lebt = true;
    (async () => {
      try {
        // Berichte mit Leserliste brauchen die Sitzung aus der Code-Anmeldung
        // (bericht-zugang.ts). Ohne Liste schickt der Server sie einfach nicht mit.
        const sitzung = leseSitzung(token);
        const res = await fetch(`${SUPABASE_URL}/functions/v1/report?t=${encodeURIComponent(token)}`, {
          headers: { Accept: 'application/json', ...(sitzung ? { 'x-bericht-sitzung': sitzung } : {}) },
        });
        if (!lebt) return;
        if (res.status === 401) {
          // Keine oder eine abgelaufene Sitzung, oder die Adresse ist nicht mehr auf der Liste.
          if (sitzung) vergissSitzung(token);
          setLage('geschuetzt');
          return;
        }
        if (!res.ok) { setLage('nicht-gefunden'); return; }
        setData(await res.json());
        setLage('da');
      } catch {
        if (lebt) setLage('nicht-gefunden');
      }
    })();
    return () => { lebt = false; };
  }, [token, angemeldet]);

  useEffect(() => {
    document.title = data
      ? t(`${data.study?.name ?? 'Bericht'} — Zielgruppen-Simulation`, `${data.study?.name ?? 'Report'} · Audience simulation`)
      : t('Bericht', 'Report');
  }, [data]);

  if (lage === 'laedt') {
    return (
      <Papier>
        <div role="status" aria-label={t('Der Bericht wird geladen', 'Loading the report')} className="space-y-4">
          <div className="h-3 w-40 rounded bg-[#e3e8e6] motion-safe:animate-pulse" />
          <div className="h-10 w-3/4 rounded bg-[#e3e8e6] motion-safe:animate-pulse" />
          <div className="h-4 w-1/2 rounded bg-[#e3e8e6] motion-safe:animate-pulse" />
          <div className="h-32 rounded bg-[#eef2f1] motion-safe:animate-pulse" />
        </div>
      </Papier>
    );
  }

  if (lage === 'geschuetzt') {
    return (
      <Papier>
        <Augenbraue>{t('Bericht', 'Report')}</Augenbraue>
        <h1 className={`${KOPF} mt-0 mb-3 text-[1.75rem] leading-tight`}>{t('Dieser Bericht ist nur für eingeladene Adressen.', 'This report is only for invited addresses.')}</h1>
        <LeserAnmeldung token={token} onAngemeldet={() => setAngemeldet(n => n + 1)} />
      </Papier>
    );
  }

  if (lage === 'anmelden') {
    return (
      <Papier>
        <Augenbraue>{t('Bericht', 'Report')}</Augenbraue>
        <h1 className={`${KOPF} mt-0 mb-3 text-[1.75rem] leading-tight`}>{t('Melde dich an, um deinen Bericht zu sehen.', 'Sign in to see your report.')}</h1>
        <p className="m-0 max-w-[60ch] leading-relaxed text-[#5f646b]">
          {t('Dieser Bericht hat keinen offenen Link. Er ist nur in deinem Konto einsehbar:', 'This report has no open link. You can only see it in your account:')}{' '}
          <a className="text-[#1f6964] underline underline-offset-[3px]" href={`/login?next=${encodeURIComponent(`/report?id=${eigeneId}`)}`}>{t('zur Anmeldung', 'sign in')}</a>.
        </p>
      </Papier>
    );
  }

  if (lage !== 'da' || !data) {
    return (
      <Papier>
        <Augenbraue>{t('Bericht', 'Report')}</Augenbraue>
        <h1 className={`${KOPF} mt-0 mb-3 text-[1.75rem] leading-tight`}>{t('Dieser Bericht ist nicht verfügbar.', 'This report is not available.')}</h1>
        <p className="m-0 max-w-[60ch] leading-relaxed text-[#5f646b]">
          {lage === 'ohne-token'
            ? t('In der Adresse fehlt die Kennung des Berichts. Lass dir den Link noch einmal schicken.', 'The address is missing the report ID. Ask for the link to be sent again.')
            : t('Der Link ist ungültig, zurückgezogen oder gehört zu einer privaten Zielgruppe. Wende dich an die Person, die ihn dir geschickt hat.', 'The link is invalid, withdrawn or belongs to a private audience. Contact the person who sent it to you.')}
        </p>
      </Papier>
    );
  }

  return <BerichtBlatt data={data} eigeneAnsicht={!token && !!eigeneId} fassungen={fassungen} />;
}

/**
 * Der geladene Bericht. Eigene Komponente, damit Tests ihn mit einer festen
 * Nutzlast rendern können (bericht-blatt-ansicht.test.tsx).
 */
export function BerichtBlatt({ data, eigeneAnsicht = false, fassungen = {} }: {
  data: any; eigeneAnsicht?: boolean; fassungen?: Record<string, FassungRef>;
}) {
  // Welche Abschnitte unter „Für alle, die genauer hinsehen“ offen stehen.
  const [offen, setOffen] = useState<Set<string>>(() => new Set());
  // Die Fassungen stehen zugeklappt. Zum Drucken klappt alles auf, sonst fehlten
  // die Texte im PDF; ein Link mit #fassung-n öffnet genau diese Fassung. Seit
  // 09.10.2026 liegen Fassungen und Empfehlungen in einem Akkordeon: der Sprung
  // klappt erst dieses auf, dann das Ziel, und scrollt danach.
  useEffect(() => {
    const auf = () => document.querySelectorAll('details[data-bericht]').forEach((d) => { (d as HTMLDetailsElement).open = true; });
    window.addEventListener('beforeprint', auf);
    const oeffne = (sanft: unknown = true) => {
      const ziel = window.location.hash ? document.getElementById(decodeURIComponent(window.location.hash.slice(1))) : null;
      if (!ziel) return;
      const akk = ziel.closest<HTMLElement>('[data-akkordeon]')?.dataset.akkordeon;
      if (akk) setOffen((o) => (o.has(akk) ? o : new Set(o).add(akk)));
      // Beim Laden springt die Seite, ein Klick im Bericht scrollt weich
      // (seit 13.10.2026, ein Leser fand den Sprung unsanft).
      const weich = sanft && !window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
      window.setTimeout(() => {
        if (ziel instanceof HTMLDetailsElement) ziel.open = true;
        ziel.scrollIntoView(weich ? { behavior: 'smooth', block: 'start' } : undefined);
      }, 0);
    };
    oeffne(false);
    // Auch ein Sprung innerhalb der Seite (Verlauf, Empfehlung) klappt die Fassung auf.
    const sprung = () => oeffne(true);
    window.addEventListener('hashchange', sprung);
    return () => { window.removeEventListener('beforeprint', auf); window.removeEventListener('hashchange', sprung); };
  }, []);

  const varianten: any[] = data.variants ?? [];
  const personas: any[] = data.set?.personas ?? [];
  const n = data.narrative ?? {};
  // Der Bericht als Vorschlag (Layout S3, BerichtVorschlag.tsx). Ohne `vorschlag` null: Layout A.
  const vorschlag = leseVorschlag(data);
  // In S3 zeigt „Design-Check im Detail“ heute und neu aus dem Vergleich, dazu die Checks der Studie.
  const designS3 = vorschlag ? designImDetail(data) : [];
  const kernpunkte: any[] = Array.isArray(n.kernpunkte) ? n.kernpunkte : [];
  const hebel: any[] = Array.isArray(n.hebel) ? n.hebel : [];
  const empfehlungen: any[] = Array.isArray(n.empfehlungen) ? n.empfehlungen : [];
  // Der Stand ist die letzte Fassung: auf sie beziehen sich Kennzahlen, Befunde und Vorschläge.
  const stand: any = standDer(data);
  const letzteVorschlaege: string[] = stand ? [...new Set<string>((stand.improvements ?? []).map((v: string) => String(v).trim()).filter(Boolean))] : [];
  // Wünsche der Personas, die eine Leitplanke des Kunden ausschließt (leitplanken-liste.tsx). Ohne Leitplanken leer.
  const kollisionen = kollisionenDesBerichts(varianten);
  const matrix = fassungsMatrix(varianten);
  const einwaende = buendleEinwaende(varianten);
  const zweifler = skeptiker(varianten);
  // Rolle und Größe hängen am Namen (personaLabel); in Listen reicht der Name.
  const namen: string[] = personas.map((p: any) => String(p?.name ?? '')).filter(Boolean);
  const kurz = (label: string) => kurzname(label, namen);
  // Stand ist der späteste Zeitpunkt: ein nachgetragener Design-Check zählt mit.
  const datum = tag(standDesBerichts(data) || data.published_at || data.generated_at);
  const erzeugt = tag(data.published_at ?? data.generated_at);
  const schritte = ablauf(data, fassungen);
  const mitDesign = Array.isArray(data.design) && data.design.length > 0;
  const setZeile = [data.set?.market, lesbaresSegment(data.set?.segment)].filter(Boolean).join(' · ');
  // Worauf die Jury beruht: Belege je Quelle, dazu wie viele Personas keinen
  // tragen. Gezählt aus den eingefrorenen Personas, also auch für alte Berichte.
  const ohneBeleg = personas.filter((p: any) => !Array.isArray(p.evidence) || p.evidence.length === 0).length;
  const quellenZeile = [
    beleglageZeile(beleglageAusPersonas(personas)),
    ohneBeleg > 0 ? t(`${ohneBeleg} von ${personas.length} ohne Beleg`, `${ohneBeleg} of ${personas.length} without evidence`) : '',
  ].filter(Boolean).join(' · ');
  // Ältere Berichte haben kein personas_im_set: dann wie bisher.
  const imSet = Number(data.set?.personas_im_set) || 0;
  const personasZeile = imSet > personas.length
    ? t(`${personas.length} von ${imSet} Personas`, `${personas.length} of ${imSet} personas`)
    : t(`${personas.length} Personas`, `${personas.length} ${personas.length === 1 ? 'persona' : 'personas'}`);
  // Urteile über alle Fassungen (seit 09.10.2026 so benannt): in der Matrix
  // urteilt eine Persona mehrmals, je Kaufanlass einmal.
  const urteile = varianten.reduce((s, v) => s + (anzahlUrteile(v) || Number(v?.wins?.total) || 0), 0);
  // Welche Empfehlung eine Persona abholt, für „Wer skeptisch bleibt".
  const empfehlungFuer = (persona: string) => {
    const name = kurz(persona).toLowerCase();
    const i = empfehlungen.findIndex((e) => Array.isArray(e?.holt_ab) && e.holt_ab.some((h: string) => {
      const x = String(h).toLowerCase();
      return x === name || name.startsWith(x) || x.startsWith(name);
    }));
    return i >= 0 ? i + 1 : null;
  };

  // Ebene 1 seit 12.10.2026: Überschrift (was und bei wem), drei Kästen, die
  // Zeitleiste „So sind wir vorgegangen“ (bericht-vorgehen.ts), dann was zu tun ist.
  const urteil = urteilDesBerichts(data);
  const umfang = umfangKurz(data);
  const titel = titelDesBerichts(data);
  const getestet = getestetSatz(data);
  const geurteilt = geurteiltSatz(data);
  const lage = ausgangslage(data);
  const gruppe = zielgruppe(data);
  const karten = fassungsKarten(data);
  const einleitung = fassungenEinleitung(data);
  const gestaltung = designSchritt(data, fassungen);
  // Der offene letzte Schritt „Nächste Runde“ (narrative.naechste_runde); ohne Feld null.
  const naechste = naechsteRunde(data);
  // Was geprüft wurde, als Bild: erster Bildschirm am Desktop und am Handy, in Schritt 1. Ohne Bilder nichts.
  const seite = gepruefteSeite(data);
  const tun = dreiZuTun(empfehlungen);
  // Ebene 2: Befunde je Abschnitt der letzten Fassung (seit 09.10.2026). Ohne
  // sie stehen dort die Empfehlungen wie bisher.
  const befunde = befundeJeAbschnitt(stand, empfehlungen);
  const mitAbschnitten = befunde.zeilen.length > 0;
  const kr = kernRandZahl(data);
  const u = data.umfang;
  const kurzMethode = Number(u?.runden) > 0
    ? t(`${u.runden} ${u.runden === 1 ? 'Runde' : 'Runden'}${Number(u.urteile) > 0 ? `, ${u.urteile} Urteile` : ''}`,
      `${u.runden} ${u.runden === 1 ? 'round' : 'rounds'}${Number(u.urteile) > 0 ? `, ${u.urteile} ${Number(u.urteile) === 1 ? 'verdict' : 'verdicts'}` : ''}`)
    : t(`${varianten.length} ${varianten.length === 1 ? 'Fassung' : 'Fassungen'}${urteile > 0 ? `, ${urteile} ${urteile === 1 ? 'Urteil' : 'Urteile'}` : ''}`,
      `${varianten.length} ${varianten.length === 1 ? 'version' : 'versions'}${urteile > 0 ? `, ${urteile} ${urteile === 1 ? 'verdict' : 'verdicts'}` : ''}`);
  const akk = (id: string, titel: string, kurzText: string, inhalt: React.ReactNode) => (
    <Akkordeon key={id} id={id} titel={titel} kurz={kurzText} offen={offen.has(id)}
      umschalten={() => setOffen((o) => { const x = new Set(o); if (x.has(id)) x.delete(id); else x.add(id); return x; })}>
      {inhalt}
    </Akkordeon>
  );

  // EBENE 3: alles Weitere, zugeklappt. Im Druck aufgeklappt. In S3 dieselben
  // Akkordeons unter den Belegen (dort plus „Befunde je Abschnitt“ und immer
  // „Alle Empfehlungen“, weil es keine Ebene 2 gibt).
  const genauer = (
    <section className="flex flex-col gap-3.5" aria-labelledby="genauer-hinsehen">
      <h2 id="genauer-hinsehen" className={`${KOPF} m-0 text-[1.5rem] leading-tight sm:text-[2rem]`}>{t('Für alle, die genauer hinsehen', 'For those who want to look closer')}</h2>
      <div className="border-b border-[#dde3e1]">
        {akk('methode', t('Methode und Umfang', 'Method and scope'), kurzMethode, (
          <>
            <dl className="m-0 grid max-w-[48rem] gap-x-6 gap-y-2 text-[0.9375rem] leading-relaxed sm:grid-cols-[9rem_minmax(0,1fr)]">
              {data.study?.offer && <Angabe titel={t('Angebot und Ziel', 'Offer and goal')}>{data.study.offer}</Angabe>}
              {typeof data.study?.markenversprechen === 'string' && data.study.markenversprechen.trim() && (
                <Angabe titel={t('Markenversprechen', 'Brand promise')}>
                  <span className="font-serif text-[1.0625rem] text-[#1d1f26]">{t('„', '“')}{data.study.markenversprechen}{t('“', '”')}</span>
                </Angabe>
              )}
              <Angabe titel={t('Grundlage', 'Basis')}>
                {(sprache() === 'en' ? [
                  data.set?.name ? `Audience “${data.set.name}”` : '',
                  kr ? `${personasZeile} (${kr.kern} from the core audience, ${kr.rand} from the extended audience)` : personasZeile,
                  `${varianten.length} ${varianten.length === 1 ? 'version' : 'versions'}${urteile > 0 ? `, ${urteile} ${urteile === 1 ? 'verdict' : 'verdicts'}` : ''} in the report`,
                  `As of ${datum}`,
                ] : [
                  data.set?.name ? `Zielgruppe „${data.set.name}“` : '',
                  kr ? `${personasZeile} (${kr.kern} aus der Kernzielgruppe, ${kr.rand} aus der erweiterten Zielgruppe)` : personasZeile,
                  `${varianten.length} ${varianten.length === 1 ? 'Fassung' : 'Fassungen'}${urteile > 0 ? `, ${urteile} ${urteile === 1 ? 'Urteil' : 'Urteile'}` : ''} im Bericht`,
                  `Stand ${datum}`,
                ]).filter(Boolean).join(' · ')}
              </Angabe>
              {kr && <Angabe titel={t('Kernzielgruppe und erweiterte Zielgruppe', 'Core audience and extended audience')}>{kernRandText()}</Angabe>}
            </dl>
            {(schritte.length > 1 || umfangZeile(data)) && <Ablauf schritte={schritte} umfang={umfangZeile(data)} />}
          </>
        ))}

        {(n.summary || kernpunkte.length > 0 || (n.recommendation && (tun.length > 0 || vorschlag))) && akk('zusammenfassung', t('Zusammenfassung', 'Summary'), kernpunkte.length
          ? t(`${kernpunkte.length === 1 ? 'eine Kernaussage' : `${anzahlWort(kernpunkte.length)} Kernaussagen`}`, kernpunkte.length === 1 ? 'one key point' : `${anzahlWort(kernpunkte.length)} key points`)
          : t('in wenigen Sätzen', 'in a few sentences'), (
          <>
            {kernpunkte.length > 0 && (
              <ol className="m-0 mb-6 grid list-none gap-3 p-0 md:grid-cols-3">
                {kernpunkte.map((k: any, i: number) => (
                  <li key={i} className="flex flex-col gap-2.5 rounded-xl border border-[#dde3e1] bg-[#ffffff] px-5 py-5 break-inside-avoid">
                    <span className="font-mono text-[0.8125rem] font-bold text-[#1f6964]">{String(i + 1).padStart(2, '0')}</span>
                    <h3 className={`${KOPF} m-0 text-[1.25rem] leading-snug`}>{k.titel}</h3>
                    {k.text && <p className="m-0 flex-1 text-[0.9375rem] leading-relaxed text-[#2f3238]">{k.text}</p>}
                    {k.zahl && (
                      <p className="m-0 flex items-baseline gap-2.5 border-t border-[#e8eceb] pt-3">
                        <span className={`${KOPF} text-[1.625rem] leading-none tabular-nums`}>{k.zahl}</span>
                        {k.zahl_text && <span className="text-[0.8125rem] text-[#5f646b]">{k.zahl_text}</span>}
                      </p>
                    )}
                  </li>
                ))}
              </ol>
            )}
            {n.summary && <p className="m-0 max-w-[62ch] text-[1.0625rem] leading-relaxed text-[#1d1f26]">{n.summary}</p>}
            {n.recommendation && (tun.length > 0 || vorschlag) && (
              <div className="mt-6 flex flex-wrap items-baseline gap-x-5 gap-y-1.5 rounded-xl bg-[#1d1f26] px-5 py-4 text-[#f4f5f2] break-inside-avoid">
                <span className="font-mono text-[0.6875rem] font-bold tracking-[0.18em] text-[#e0a46e] uppercase">{t('Empfehlung', 'Recommendation')}</span>
                <p className="m-0 min-w-0 flex-[1_1_28rem] leading-relaxed">{n.recommendation}</p>
              </div>
            )}
          </>
        ))}

        {/* Die Texte, die wir neu geschrieben haben (Robert, 07.10.2026: „müssen
            rein oder verlinkt werden“). Ein Link mit #fassung-n klappt hier auf. */}
        {varianten.length > 0 && akk('texte', varianten.length === 1 ? t('Der Text im Wortlaut', 'The text in full') : t('Die Texte im Wortlaut', 'The texts in full'),
          varianten.length === 1 ? t('neben der Vergleichsfassung', 'next to the baseline') : t(`${varianten.length} Fassungen, mit Verlauf`, `${varianten.length} versions, with progression`), (
            <>
              <div className="flex flex-col gap-4 print:hidden">
                {varianten.length > 1 && <Verlauf varianten={varianten} />}
                {varianten.length > 1 && (
                  <p className="m-0 max-w-[70ch] text-[0.8125rem] leading-relaxed text-[#5f646b]">
                    {t(<>
                      „{zahl(stand?.scores?.candidate)} zu {zahl(stand?.scores?.baseline)}“ ist die Durchschnittsnote der geprüften Fassung gegen die
                      der Vergleichsfassung, von 0 bis 10{varianten.some((v: any) => leseKernRand(v)) ? ', gezählt im Kern' : ''}.
                    </>, <>
                      “{zahl(stand?.scores?.candidate)} to {zahl(stand?.scores?.baseline)}” is the average score of the tested version against
                      that of the baseline, from 0 to 10{varianten.some((v: any) => leseKernRand(v)) ? ', counted in the core' : ''}.
                    </>)}
                  </p>
                )}
                <div className="overflow-hidden rounded-xl border border-[#dde3e1] bg-[#ffffff]">
                  {varianten.map((x: any, i: number) => <Fassung key={x.run_id ?? i} x={x} nr={i + 1} mehrere={varianten.length > 1} />)}
                </div>
              </div>
              <p className="m-0 hidden text-[0.9375rem] text-[#2f3238] print:block">{t('Im Wortlaut im Anhang, jede Fassung einmal und die Vergleichsfassung am Ende.', 'In full in the appendix, each version once and the baseline at the end.')}</p>
            </>
          ))}

        {(matrix.spalten.length > 1 || (stand?.mode !== 'feedback' && personaNoten(stand).length > 0)) && akk('personas', t('Persona für Persona', 'Persona by persona'),
          kr ? t(`${kr.kern} Kern, ${kr.rand} erweitert`, `${kr.kern} core, ${kr.rand} extended`) : personasZeile, (
            matrix.spalten.length > 1
              ? <><h3 className={`${KOPF} mt-0 mb-4 text-[1.25rem] leading-tight`}>{t('Abstand zur Vergleichsfassung, je Persona', 'Gap to the baseline, by persona')}</h3><PersonaMatrix m={matrix} kurz={kurz} /></>
              : <NotenJePersona quelle={stand} />
          ))}

        {hebel.length > 0 && akk('zieht', t('Was zieht', 'What works'), t(`${hebel.length} ${hebel.length === 1 ? 'Muster' : 'Muster'}, mit Stimmen`, `${hebel.length} ${hebel.length === 1 ? 'pattern' : 'patterns'}, with voices`), (
          <ul className="m-0 grid list-none gap-3 p-0 md:grid-cols-2">
            {hebel.map((h: any, i: number) => (
              <Muster key={i} titel={h.titel} text={h.text} personas={(h.personas ?? []).map(kurz)} gesamt={personas.length} farbe="blau"
                stimme={typeof h.zitat?.text === 'string' && h.zitat.text ? { text: h.zitat.text, persona: kurz(String(h.zitat.persona ?? '')) } : undefined} />
            ))}
          </ul>
        ))}

        {einwaende.length > 0 && akk('einwaende', t('Alle Einwände', 'All objections'), t(`${einwaende.length}, nach Häufigkeit`, `${einwaende.length}, by frequency`), (
          <>
            <ul className="m-0 grid list-none gap-3 p-0 md:grid-cols-2">
              {einwaende.map((e, i) => (
                <Muster key={i} titel={e.einwand} zitat personas={e.personas.map(kurz)} gesamt={personas.length} farbe="rost" offenIn={varianten.length > 1 ? e.offenIn : undefined} nurRand={e.nurRand} />
              ))}
            </ul>
            <p className="mt-3 mb-0 text-[0.8125rem] text-[#5f646b]">
              {t('Gezählt werden Personas, nicht Nennungen. Einwände in anderen Worten sind über gemeinsame Begriffe zusammengefasst.', 'Personas are counted, not mentions. Objections in different words are grouped by shared terms.')}
            </p>
          </>
        ))}

        {zweifler.length > 0 && akk('skeptisch', t('Wer skeptisch bleibt', 'Who stays skeptical'), t(`${zweifler.length === 1 ? 'eine Kernpersona' : `${anzahlWort(zweifler.length)} Kernpersonas`}`, zweifler.length === 1 ? 'one core persona' : `${anzahlWort(zweifler.length)} core personas`), (
          <>
            <h3 className={`${KOPF} mt-0 mb-4 text-[1.25rem] leading-tight`}>{t(<>Auch die letzte Fassung holt {zweifler.length === 1 ? 'eine Kernpersona' : `${anzahlWort(zweifler.length)} Kernpersonas`} nicht ab</>, <>Even the last version does not win over {zweifler.length === 1 ? 'one core persona' : `${anzahlWort(zweifler.length)} core personas`}</>)}</h3>
            <ul className="m-0 flex list-none flex-col gap-3 p-0">
              {zweifler.map((s) => {
                const e = empfehlungFuer(s.persona);
                return (
                  <li key={s.persona} className={`grid overflow-hidden rounded-xl border border-[#dde3e1] bg-[#ffffff] break-inside-avoid ${e != null ? 'md:grid-cols-[3fr_2fr]' : ''}`}>
                    <div className="max-w-[72ch] px-5 py-4">
                      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5">
                        <span className="font-semibold text-[#1d1f26]" title={s.persona}>{kurz(s.persona)}</span>
                        <span className="text-[0.8125rem] text-[#5f646b] tabular-nums">{zahl(s.geprueft)} {t('zu', 'to')} {zahl(s.vergleich)}</span>
                      </div>
                      {s.einwand && <p className="mt-1.5 mb-0 text-[1.0625rem] leading-snug text-[#1d1f26] italic font-serif">{zitiert(s.einwand)}</p>}
                      {s.unklar && <p className="mt-1 mb-0 text-sm text-[#5f646b]">{t('Unklar', 'Unclear')}: {s.unklar}</p>}
                    </div>
                    {e != null && (
                      <div className="flex items-center bg-[#eef2f1] px-5 py-4">
                        <a href={`#empfehlung-${e}`} className="font-semibold text-[#1f6964] no-underline hover:underline">→ {t('Empfehlung', 'Recommendation')} {e}: {empfehlungen[e - 1]?.titel}</a>
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          </>
        ))}

        {vorschlag && mitAbschnitten && akk('abschnitte', t('Befunde je Abschnitt', 'Findings by section'), t(`${befunde.zeilen.length} Abschnitte der neuen Fassung`, `${befunde.zeilen.length} sections of the new version`), (
          <>
            <p className="mt-0 mb-2 text-[0.8125rem] text-[#5f646b]">{nennerSatz(befunde)}.</p>
            <AbschnittsListe zeilen={befunde.zeilen} kurz={kurz} ebene={4} />
            {befunde.ohneAbschnitt.length > 0 && (
              <p className="mt-4 mb-0 text-[0.8125rem] leading-relaxed text-[#5f646b]">
                {t('Keinem Abschnitt zugeordnet:', 'Not assigned to a section:')}{' '}
                {befunde.ohneAbschnitt.map((nr, i) => <span key={nr}>{i > 0 && ', '}<a className="text-[#1f6964] underline underline-offset-2" href={`#empfehlung-${nr}`}>{t('Empfehlung', 'Recommendation')} {nr}</a></span>)}.
              </p>
            )}
          </>
        ))}

        {/* In S3 ohne „vor dem Livegang prüfen“: der Abschnitt mit To-dos vor dem Livegang ist dort gestrichen (Robert, 09.10.2026). */}
        {(mitAbschnitten || vorschlag) && empfehlungen.length > 0 && akk('empfehlungen', t('Alle Empfehlungen', 'All recommendations'), t(`${empfehlungen.length}, die wichtigste zuerst`, `${empfehlungen.length}, most important first`), (
          <EmpfehlungsListe empfehlungen={empfehlungen} vorschlagEtikett={vorschlag ? t('Formulierungsvorschlag', 'Suggested wording') : undefined} />
        ))}

        {/* Wünsche, die eine Leitplanke des Kunden ausschließt: nicht empfohlen, zur Entscheidung vorgelegt. */}
        {kollisionen.length > 0 && akk('leitplanken', t('Kollidiert mit euren Leitplanken', 'Conflicts with your guardrails'), t(`${personenMitKollision(kollisionen) === 1 ? 'eine Persona' : `${personenMitKollision(kollisionen)} Personas`}, ihr entscheidet`, `${personenMitKollision(kollisionen) === 1 ? 'one persona' : `${personenMitKollision(kollisionen)} personas`}, you decide`), (
          <>
            <h3 className={`${KOPF} mt-0 mb-4 text-[1.25rem] leading-tight`}>{leitplankenTitel(kollisionen)}</h3>
            <LeitplankenListe kollisionen={kollisionen} />
          </>
        ))}

        {data.besetzt?.aussagen?.length > 0 && akk('besetzt', t('Was nur ihr sagt', 'What only you say'), t(`${data.besetzt.aussagen.length} Kernaussagen gegen den Wettbewerb`, `${data.besetzt.aussagen.length} key claims against the competition`), (
          <>
            <h3 className={`${KOPF} mt-0 mb-4 text-[1.25rem] leading-tight`}>{besetztTitel(data.besetzt)}</h3>
            <BesetztListe b={data.besetzt} />
          </>
        ))}

        {/* Design-Checks der Studie (seit 08.10.2026, bericht_design.ts). Nur
            Noten, Prüferbefunde und Bilder, keine Eindrücke der Personas. */}
        {vorschlag && designS3.length > 0 && akk('design', t('Design-Simulation im Detail', 'Design simulation in detail'), designS3.some((e) => e.d?.handwerk) ? t('Noten, Fachprüfer, Bilder', 'Scores, expert reviewer, images') : t('Noten und Bilder', 'Scores and images'), (
          <>
            <dl className="mt-0 mb-8 grid max-w-[52rem] gap-x-6 gap-y-2 text-[0.9375rem] leading-relaxed sm:grid-cols-[9rem_minmax(0,1fr)]">
              <Angabe titel={t('Noten', 'Scores')}>{notenText()}</Angabe>
              {designS3.some((e) => e.d?.handwerk) && (
                <>
                  <Angabe titel={t('Fachprüfer', 'Expert reviewer')}>{fachprueferText()}</Angabe>
                  <Angabe titel={t('Grad', 'Grade')}>{gradText()}</Angabe>
                </>
              )}
            </dl>
            <div className="space-y-12">
              {designS3.map((e, i) => (
                <div key={i}>
                  <h4 className={`${KOPF} mt-0 mb-3 text-[1.25rem] leading-tight`}>{e.titel}</h4>
                  <DesignCheck d={e.d} fassung={fassungen[e.d?.skizze?.fassung_hash ?? '']} ebene={5} />
                </div>
              ))}
            </div>
            <p className="mt-6 mb-0 text-[0.8125rem] leading-relaxed text-[#5f646b]">
              {t('Simuliert und nicht kalibriert; die Noten fließen in keinen Text-Score ein.', 'Simulated and not calibrated; the scores do not feed into any text score.')}
            </p>
          </>
        ))}

        {!vorschlag && mitDesign && akk('design', t('Design-Simulation im Detail', 'Design simulation in detail'), data.design.some((d: any) => d?.handwerk) ? t('Noten, Fachprüfer, Bilder', 'Scores, expert reviewer, images') : t('Noten und Bilder', 'Scores and images'), (
          <>
            <h3 className={`${KOPF} mt-0 mb-4 text-[1.25rem] leading-tight`}>
              {designEintraege(data.design).some((e) => e.art === 'paar')
                ? t('Heute und mit der neuen Fassung', 'Today and with the new version')
                : data.design.length === 1 ? t('Design-Check der Seite', 'Design check of the page') : t('Design-Checks der Seiten', 'Design checks of the pages')}
            </h3>
            <dl className="mt-0 mb-8 grid max-w-[52rem] gap-x-6 gap-y-2 text-[0.9375rem] leading-relaxed sm:grid-cols-[9rem_minmax(0,1fr)]">
              <Angabe titel={t('Noten', 'Scores')}>{notenText()}</Angabe>
              {data.design.some((d: any) => d?.handwerk) && (
                <>
                  <Angabe titel={t('Fachprüfer', 'Expert reviewer')}>{fachprueferText()}</Angabe>
                  <Angabe titel={t('Grad', 'Grade')}>{gradText()}</Angabe>
                </>
              )}
            </dl>
            <div className="space-y-12">
              {designEintraege(data.design).map((e, i) => e.art === 'paar'
                ? <VorherNachher key={i} heute={e.heute} skizze={e.skizze} fassung={fassungen[e.skizze?.skizze?.fassung_hash ?? '']} />
                : <DesignCheck key={i} d={e.d} fassung={fassungen[e.d?.skizze?.fassung_hash ?? '']} />)}
            </div>
            <p className="mt-6 mb-0 text-[0.8125rem] leading-relaxed text-[#5f646b]">
              {t('Simuliert und nicht kalibriert; die Noten fließen in keinen Text-Score ein.', 'Simulated and not calibrated; the scores do not feed into any text score.')}
            </p>
          </>
        ))}

        {/* Wer geurteilt hat. Nur Rolle, Groesse und Belege: mehr gibt die
            Nutzlast bewusst nicht heraus (report.ts, „Was nicht in die Nutzlast gehoert"). */}
        {/* Erster Bildschirm im Vergleich (test_first_screen, seit 09.10.2026,
            bericht_design.ts). Bilder und Klickanteile, keine Stimmen. */}
        {Array.isArray(data.erster_bildschirm) && data.erster_bildschirm.map((eb: any, i: number) => akk(`erster-bildschirm-${i + 1}`, t('Above the Fold im Vergleich', 'Above the fold compared'),
          t(`${Array.isArray(eb?.varianten) ? eb.varianten.length : 0} Varianten, Klickanteile und Bilder`, `${Array.isArray(eb?.varianten) ? eb.varianten.length : 0} variants, click shares and images`), (
          <>
            <h3 className={`${KOPF} mt-0 mb-4 text-[1.25rem] leading-tight`}>{ersterBildschirmTitel(eb)}</h3>
            <ErsterBildschirm t={eb} />
          </>
        )))}

        {personas.length > 0 && akk('wer', t('Wer urteilt', 'Who judges'), t(`${personasZeile} mit Belegen`, `${personasZeile} with evidence`), (
          <>
            <h3 className={`${KOPF} mt-0 mb-2 text-[1.25rem] leading-tight`}>{t(<>{personasZeile} aus der Zielgruppe „{data.set?.name}“</>, <>{personasZeile} from the audience “{data.set?.name}”</>)}</h3>
            {(setZeile || quellenZeile) && (
              <p className="mt-0 mb-5 text-[0.9375rem] text-[#5f646b]">
                {[setZeile, quellenZeile].filter(Boolean).join(' · ')}
              </p>
            )}
            <ul className="m-0 grid list-none gap-3 p-0 sm:grid-cols-2 lg:grid-cols-3">
              {personas.map((p: any, i: number) => <PersonaKarte key={i} p={p} />)}
            </ul>
          </>
        ))}
      </div>
    </section>
  );

  const fuss = (
    <footer className={vorschlag
      ? 'flex flex-col gap-4 border-t border-[#dde3e1] pt-[18px] text-sm leading-relaxed text-[#5f646b] break-inside-avoid'
      : 'mt-12 flex flex-col gap-4 pt-2 text-sm leading-relaxed text-[#5f646b] break-inside-avoid'}>
      <p className="m-0 max-w-[70ch] text-[0.8125rem] leading-normal text-[#5f646b]">
        {t(<>
          Simulierte Reaktionen recherchierter Personas, kein Test mit echten Besuchern. Der Text-Vergleich liest den Text und wie
          prominent er steht; {mitDesign || designS3.length > 0 ? 'wie die Seite aussieht, prüfen die Design-Checks getrennt davon.' : 'wie die Seite aussieht, bewertet er nicht.'} Ob
          die Seite mehr Anfragen bringt, zeigen erst die Zahlen nach dem Livegang.
        </>, <>
          Simulated reactions of researched personas, not a test with real visitors. The text comparison reads the text and how
          prominently it appears; {mitDesign || designS3.length > 0 ? 'how the page looks is checked separately by the design checks.' : 'it does not rate how the page looks.'} Whether
          the page brings more inquiries only shows in the numbers after launch.
        </>)}
      </p>
      <p className="m-0 max-w-[70ch] text-[0.8125rem]">
        {t(<>
          Erzeugt am {erzeugt}{datum !== erzeugt ? `, ergänzt am ${datum}` : ''}. Jeder Einwand
          stammt aus einem einzelnen Persona-Run. Quellenangaben werden serverseitig gegen die
          hinterlegten Belege geprüft; was sich nicht belegen lässt, erscheint ohne Quelle statt mit
          einer behaupteten.
        </>, <>
          Created on {erzeugt}{datum !== erzeugt ? `, updated on ${datum}` : ''}. Each objection
          comes from a single persona run. Sources are checked on the server against the
          stored evidence; whatever cannot be backed up appears without a source rather than with
          a claimed one.
        </>)}
      </p>
      <p className="m-0 text-[0.8125rem]">
        {t('Erstellt mit', 'Made with')} <a className="text-[#1f6964] underline underline-offset-[3px]" href="https://www.99takes.com">99takes</a>.
      </p>
      {/* Der Bericht wird weitergeleitet; als PDF laesst er sich ablegen und
          anhaengen. Der Browser erzeugt es, die Druckregeln oben sorgen fuer A4. */}
      <button
        type="button" onClick={() => window.print()}
        className="min-h-11 cursor-pointer self-start rounded-md border border-[#cbd3d1] bg-[#f4f5f2] px-3 py-1.5 text-[0.8125rem] font-medium text-[#1d1f26] hover:border-[#3f8a87] hover:text-[#1f6964] print:hidden"
      >
        {t('Drucken oder als PDF sichern', 'Print or save as PDF')}
      </button>
    </footer>
  );

  const kopfzeile = <>{data.study?.name}<span className="mx-2 text-[#bcc5c3]">·</span>{t('Stand', 'As of')} {datum}</>;
  const eigeneHinweis = eigeneAnsicht && (
    <p className="mb-8 rounded-md border border-[#cbd3d1] bg-[#eef2f1] px-4 py-3 text-sm leading-relaxed text-[#5f646b] print:hidden">
      {data.shared
        ? t('Deine Ansicht. Dieser Bericht ist außerdem über einen offenen Link erreichbar. Zurückziehen kannst du ihn im Konto unter Runs.', 'Your view. This report can also be reached through an open link. You can withdraw it in your account under Runs.')
        : t('Nur für dich sichtbar. Dieser Bericht hat keinen offenen Link.', 'Only visible to you. This report has no open link.')}
    </p>
  );
  // Testlauf (bericht-testlauf.ts): auch im Druck, damit kein PDF ohne Vermerk entsteht.
  const testlauf = testlaufSatz(data);
  const testlaufHinweis = testlauf && (
    <p role="alert" className="mb-8 rounded-md border-2 border-[#c06c28] bg-[#fbeee2] px-4 py-3 text-sm font-semibold leading-relaxed text-[#7a3d0c]">
      {testlauf}
    </p>
  );

  // Layout S3: erst der Vorschlag, dann die Belege, dann dieselben Akkordeons.
  if (vorschlag) {
    // Ohne `kopf`: S3 hat eine eigene Kopfzeile mit Studie und Stand, sonst stünden beide zweimal untereinander.
    return (
      <Papier breit>
        {testlaufHinweis}
        {eigeneHinweis}
        <BerichtVorschlag data={data} v={vorschlag} datum={datum} details={genauer} fuss={fuss} />
        <AnhangTexte varianten={varianten} />
      </Papier>
    );
  }

  return (
    <Papier kopf={kopfzeile}>
      {testlaufHinweis}
      {eigeneHinweis}

      {/* EBENE 1 seit 12.10.2026: was getestet wurde und bei wem, drei Kästen,
          dann Schritt für Schritt zum Ergebnis (bericht-vorgehen.ts). */}
      <header className="mb-11 flex flex-col gap-3.5 print:mb-8">
        <Kick>{t('Zielgruppen-Simulation', 'Audience simulation')}{titel !== data.study?.name && data.study?.name ? ` · ${data.study.name}` : ''} · {datum}</Kick>
        <h1 className={`${KOPF} m-0 max-w-[24ch] text-[1.875rem] leading-[1.12] sm:text-[2.875rem] sm:leading-[1.1] [text-wrap:balance]`}>{titel}</h1>
        <dl className="mt-2 mb-0 grid overflow-hidden rounded-xl border border-[#dde3e1] sm:grid-cols-3 print:grid-cols-3">
          {getestet && <Kasten titel={t('Getestet', 'Tested')}>{getestet}</Kasten>}
          {geurteilt && <Kasten titel={t('Geurteilt haben', 'Judged by')}>{geurteilt}</Kasten>}
          <Kasten titel={t('Ergebnis', 'Result')} betont>
            {urteil ?? (stand ? <ErgebnisSatz v={stand} /> : ersterSatz(data) ?? '')}
          </Kasten>
        </dl>
      </header>

      <section aria-labelledby="vorgehen" className="mb-14 flex flex-col gap-2 print:mb-10">
        <h2 id="vorgehen" className={`${KOPF} mt-0 mb-3 text-[1.375rem] leading-tight sm:text-[1.75rem]`}>{t('So sind wir vorgegangen, und was jeder Schritt ergab', 'How we went about it, and what each step showed')}</h2>
        <ol className="m-0 flex list-none flex-col p-0">
          {(() => {
            const schritte: Partial<Record<SchrittArt, { titel: string; inhalt: React.ReactNode }>> = {};
            if (lage.saetze.length || seite) {
              schritte.lage = {
                titel: lage.titel,
                inhalt: (
                  <>
                    {lage.saetze.map((s, i) => <p key={i} className="m-0 max-w-[64ch] text-[0.9375rem] leading-relaxed text-[#565b62]">{s}</p>)}
                    {seite && <GepruefteSeiteBlock s={seite} />}
                  </>
                ),
              };
            }
            if (gruppe) {
              schritte.zielgruppe = {
                titel: t('Zielgruppe recherchiert', 'Audience researched'),
                inhalt: (
                  <>
                    <p className="m-0 max-w-[64ch] text-[0.9375rem] leading-relaxed text-[#565b62]">
                      {t(
                        <>{gruppe.personas} Personas{gruppe.belege ? `, gestützt auf ${gruppe.belege} ${gruppe.belege === 1 ? 'Beleg' : 'Belege'} aus echten Quellen` : ''}{gruppe.erweitert ? ', in zwei Gruppen:' : '. Alle zählen ins Urteil.'}</>,
                        <>{gruppe.personas} {gruppe.personas === 1 ? 'persona' : 'personas'}{gruppe.belege ? `, backed by ${gruppe.belege} ${gruppe.belege === 1 ? 'piece' : 'pieces'} of evidence from real sources` : ''}{gruppe.erweitert ? ', in two groups:' : '. All count toward the verdict.'}</>,
                      )}
                    </p>
                    {gruppe.erweitert && (
                      <div className="grid gap-2.5 sm:grid-cols-2 print:grid-cols-2">
                        <div className="flex flex-col gap-1 rounded-[10px] border border-[#dde3e1] px-3.5 py-3 break-inside-avoid">
                          <span className="text-[0.9375rem] font-semibold text-[#1d1f26]">{gruppe.kern} {t('aus der Kernzielgruppe', 'from the core audience')}</span>
                          <span className="text-[0.875rem] leading-normal text-[#565b62]">{t('Für sie ist die Seite gemacht. Ihr Urteil entscheidet.', 'The page is made for them. Their verdict decides.')}</span>
                        </div>
                        <div className="flex flex-col gap-1 rounded-[10px] border border-dashed border-[#b7c0be] px-3.5 py-3 break-inside-avoid">
                          <span className="text-[0.9375rem] font-semibold text-[#1d1f26]">{gruppe.erweitert.n} {t('aus der erweiterten Zielgruppe', 'from the extended audience')}</span>
                          <span className="text-[0.875rem] leading-normal text-[#565b62]">
                            {gruppe.erweitert.rollen.length ? `${aufzaehlung(gruppe.erweitert.rollen)}. ` : t('Angrenzende Rollen. ', 'Adjacent roles. ')}{t('Sie lesen mit, zählen aber nicht ins Urteil.', 'They read along but do not count toward the verdict.')}
                          </span>
                        </div>
                      </div>
                    )}
                  </>
                ),
              };
            }
            // Welche Schritte es gibt, steht vor ihrem Inhalt fest: der Kasten
            // „Gestaltung“ in Schritt 3 nennt die Nummer des Gestaltungs-Schritts.
            const mitErgebnis = karten.some((k) => k.stufe || k.weiter);
            const erster = ersterBildschirmSchritt(data);
            if (erster) schritte.erster_bildschirm = { titel: erster.titel, inhalt: <ErsterBildschirmBlock s={erster} /> };
            const folge = schrittFolge({
              lage: !!schritte.lage, zielgruppe: !!schritte.zielgruppe, erster_bildschirm: !!erster, fassungen: karten.length > 0, ergebnis: karten.length > 0 && mitErgebnis,
              gestaltung: !!gestaltung, naechste_runde: !!naechste,
            });
            const gestaltungNr = folge.find((p) => p.art === 'gestaltung')?.nr ?? null;
            if (karten.length) {
              schritte.fassungen = {
                titel: karten.length === 1 ? t('Fassung getestet', 'Version tested') : t('Fassungen getestet', 'Versions tested'),
                inhalt: (
                  <>
                    {einleitung.frage && <p className="m-0 max-w-[64ch] text-[0.9375rem] leading-relaxed text-[#1d1f26]">{t('Die Frage dahinter:', 'The question behind it:')} {einleitung.frage}</p>}
                    <p className="m-0 max-w-[64ch] text-[0.9375rem] leading-relaxed text-[#565b62]">{einleitung.satz}</p>
                    <PruefungenBlock p={pruefungen(gestaltung, gestaltungNr)} />
                    <div className={`mt-1 grid gap-3 md:grid-cols-2 print:grid-cols-2 ${karten.length === 3 ? 'lg:grid-cols-3' : ''}`}>
                      {karten.map((k) => <FassungsKarteAnsicht key={k.nr} k={k} link={leseLink(data, varianten[k.nr - 1])} />)}
                    </div>
                  </>
                ),
              };
              if (mitErgebnis) {
                schritte.ergebnis = {
                  titel: t('Text-Simulation: das Ergebnis', 'Text simulation: the result'),
                  inhalt: (
                    <>
                      <ErgebnisListe karten={ergebnisReihe(karten)} />
                      {stand && <KernUndMarke v={stand} nurHinweise ohneWarnung vergleich={vergleichFuerSatz(data, stand)} />}
                    </>
                  ),
                };
              }
            }
            if (gestaltung) {
              const erst = karten.length > 0
                ? t('Erst hier sehen die Personas die Seite als Bild', 'Only here do the personas see the page as an image')
                : t('Hier sehen die Personas die Seite als Bild', 'Here the personas see the page as an image');
              schritte.gestaltung = {
                titel: gestaltung.titel,
                inhalt: gestaltung.art === 'paar' ? (
                  <>
                    <p className="m-0 max-w-[64ch] text-[0.9375rem] leading-relaxed text-[#565b62]">
                      {t(
                        <>{erst}. Wir haben {gestaltung.name ? `„${gestaltung.name}“` : 'die neue Fassung'} in die Seite gesetzt, und jede Persona hat sie durchgescrollt, am Desktop und am Handy. Verglichen mit der Seite, wie sie heute ist:</>,
                        <>{erst}. We put {gestaltung.name ? `“${gestaltung.name}”` : 'the new version'} into the page, and each persona scrolled through it, on desktop and on mobile. Compared with the page as it is today:</>,
                      )}
                    </p>
                    <div className="flex flex-col gap-2">
                      {gestaltung.stufen.map((g) => (
                        <div key={g.geraet} className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
                          <span className="w-16 text-[0.875rem] text-[#565b62]">{geraetName(g.geraet)}</span>
                          <Skala stufe={g.stufe} />
                          <span className="text-[0.875rem] font-semibold text-[#1d1f26]">{g.wort}</span>
                        </div>
                      ))}
                    </div>
                    <NotenHierBlock g={gestaltung} />
                  </>
                ) : (
                  <>
                    <p className="m-0 max-w-[64ch] text-[0.9375rem] leading-relaxed text-[#565b62]">
                      {erst}{t(', am Desktop und am Handy. ', ', on desktop and on mobile. ')}{/Mittel|average|mean/i.test(gestaltung.was) ? t('Im Mittel der Noten', 'Average of the scores') : t('Erster Eindruck', 'First impression')}: <span className="whitespace-nowrap">{zahl(gestaltung.note)} {t('von 10', 'out of 10')}</span>.{gestaltung.satz ? ` ${gestaltung.satz}` : ''}
                    </p>
                    <a href="#akkordeon-design" className="self-start text-[0.875rem] font-semibold text-[#1f6964] underline-offset-[3px] hover:underline print:hidden">{t('Alle Noten im Detail', 'All scores in detail')}</a>
                  </>
                ),
              };
            }
            if (naechste) {
              schritte.naechste_runde = {
                titel: t('Nächste Runde', 'Next round'),
                inhalt: <NaechsteRundeBlock r={naechste} />,
              };
            }
            return folge.map((p) => (
              <Zeitschritt key={p.art} nr={p.nr} titel={schritte[p.art]!.titel} letzter={p.letzter} offen={p.offen}>{schritte[p.art]!.inhalt}</Zeitschritt>
            ));
          })()}
        </ol>

        {(karten.some((k) => k.stufe) || gestaltung?.art === 'paar') && (
          <details data-bericht className="group mt-3 border-y border-[#dde3e1]">
            <summary className="flex min-h-[52px] cursor-pointer list-none items-center gap-3 py-2 text-base font-semibold text-[#1d1f26] [&::-webkit-details-marker]:hidden">
              <svg className="shrink-0 transition-transform duration-200 group-open:rotate-90 motion-reduce:transition-none" width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="#1f6964" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden><path d="M6 3l5 5-5 5" /></svg>
              {t('Wie die Stufen entstehen', 'How the levels are derived')}
            </summary>
            <StufenHerleitung data={data} karten={karten} gestaltung={gestaltung} umfang={umfang} />
          </details>
        )}
      </section>

      {tun.length > 0 ? (
        <section className="mb-14 flex flex-col gap-4 print:mb-10" aria-label={t('Was ihr jetzt tun solltet', 'What you should do now')}>
          <Kick>{t('Was ihr jetzt tun solltet', 'What you should do now')}</Kick>
          <ol className="m-0 grid list-none gap-5 p-0 md:grid-cols-3">
            {tun.map((td) => (
              <li key={td.nr} className="flex flex-col gap-1.5 border-t-2 border-[#1d1f26] pt-3.5 break-inside-avoid">
                <span className={`${KOPF} text-[1.25rem] leading-snug sm:text-[1.375rem]`}>{td.nr}. {td.titel}</span>
                {td.zeile && <span className="text-[0.90625rem] leading-relaxed text-[#565b62]">{td.zeile}</span>}
                {empfehlungen[td.nr - 1]?.kollidiert_mit && (
                  <span className="text-[0.8125rem] leading-snug text-[#1f6964]">{t('Kollidiert mit Leitplanke:', 'Conflicts with guardrail:')} {String(empfehlungen[td.nr - 1].kollidiert_mit)}</span>
                )}
              </li>
            ))}
          </ol>
          {empfehlungen.length > 3 && (
            <a href={mitAbschnitten ? '#akkordeon-empfehlungen' : '#empfehlung-4'} className="self-start text-[0.875rem] font-semibold text-[#1f6964] underline-offset-[3px] hover:underline print:hidden">
              {t(`Alle ${empfehlungen.length} Empfehlungen mit Vorschlag`, `All ${empfehlungen.length} recommendations with suggestions`)}
            </a>
          )}
        </section>
      ) : n.recommendation ? (
        <section className="mb-14 flex flex-col gap-3 print:mb-10" aria-label={t('Was ihr jetzt tun solltet', 'What you should do now')}>
          <Kick>{t('Was ihr jetzt tun solltet', 'What you should do now')}</Kick>
          <p className="m-0 max-w-[62ch] border-t-2 border-[#1d1f26] pt-3.5 text-[1.0625rem] leading-relaxed text-[#1d1f26]">{n.recommendation}</p>
        </section>
      ) : null}

      {/* Leitplanken des Kunden (leitplanken-liste.tsx): in Ebene 1 nur ein Satz,
          die Liste steht unten im Akkordeon. Ohne Kollisionen nichts. */}
      {kollisionen.length > 0 && (
        <p className="mb-14 max-w-[62ch] rounded-lg border border-[#e0a46e] bg-[#fdf3ea] px-4 py-3 text-[0.9375rem] leading-relaxed text-[#1d1f26] print:mb-10">
          <b>{t('Kollidiert mit euren Leitplanken:', 'Conflicts with your guardrails:')}</b> {leitplankenTitel(kollisionen)} {t('Wir empfehlen das nicht, ihr entscheidet.', 'We do not recommend this, you decide.')}{' '}
          <a href="#akkordeon-leitplanken" className="whitespace-nowrap font-semibold text-[#1f6964] underline-offset-[3px] hover:underline print:hidden">{t('Zur Liste', 'To the list')}</a>
        </p>
      )}

      {/* EBENE 2: je Abschnitt der Seite, in ihrer Reihenfolge. Ohne Abschnitte
          (Runs vor dem 09.10.2026) die Empfehlungen wie bisher. */}
      {mitAbschnitten ? (
        <section className="mb-16 flex flex-col print:mb-10" aria-labelledby="befunde-je-abschnitt">
          <div className="flex flex-wrap items-baseline justify-between gap-x-5 gap-y-1 border-b border-[#dde3e1] pb-3">
            <h2 id="befunde-je-abschnitt" className={`${KOPF} m-0 text-[1.5rem] leading-tight sm:text-[2rem]`}>{t('Befunde je Abschnitt', 'Findings by section')}</h2>
            <span className="text-[0.8125rem] text-[#5f646b]">{nennerSatz(befunde)}</span>
          </div>
          <AbschnittsListe zeilen={befunde.zeilen} kurz={kurz} />
          {befunde.ohneAbschnitt.length > 0 && (
            <p className="mt-4 mb-0 text-[0.8125rem] leading-relaxed text-[#5f646b]">
              {t('Keinem Abschnitt zugeordnet:', 'Not assigned to a section:')}{' '}
              {befunde.ohneAbschnitt.map((nr, i) => <span key={nr}>{i > 0 && ', '}<a className="text-[#1f6964] underline underline-offset-2" href={`#empfehlung-${nr}`}>{t('Empfehlung', 'Recommendation')} {nr}</a></span>)}.
            </p>
          )}
        </section>
      ) : empfehlungen.length > 0 ? (
        <section className="mb-16 flex flex-col gap-4 print:mb-10">
          <h2 className={`${KOPF} m-0 text-[1.5rem] leading-tight sm:text-[2rem]`}>{t('Was wir konkret empfehlen', 'What we specifically recommend')}</h2>
          <EmpfehlungsListe empfehlungen={empfehlungen} />
        </section>
      ) : letzteVorschlaege.length > 0 ? (
        <section className="mb-16 flex flex-col gap-2 print:mb-10">
          <h2 className={`${KOPF} m-0 text-[1.5rem] leading-tight sm:text-[2rem]`}>{t('Was die Personas stattdessen bräuchten', 'What the personas would need instead')}</h2>
          <ol className="m-0 list-none p-0">
            {letzteVorschlaege.map((v, i) => (
              <li key={i} className="grid grid-cols-[2.5rem_1fr] gap-4 border-t border-[#1d1f26]/10 py-4 first:border-t-0 break-inside-avoid sm:grid-cols-[3.5rem_1fr]">
                <span className={`${KOPF} text-[2rem] leading-none font-medium tracking-[-0.04em] text-[#3f8a87] tabular-nums sm:text-[2.5rem]`}>{i + 1}</span>
                <div className="min-w-0">
                  <p className="m-0 max-w-[62ch] pt-1 leading-relaxed text-[#1d1f26]">{v}</p>
                  {kollidiert(v, kollisionen) && <KollisionsHinweis leitplanke={kollidiert(v, kollisionen)!} />}
                </div>
              </li>
            ))}
          </ol>
        </section>
      ) : null}

      {genauer}

      {fuss}

      {/* Nach der Lesart, damit sie im PDF nicht hinter 15 Seiten Text steht. */}
      <AnhangTexte varianten={varianten} />
    </Papier>
  );
}

/** Ueberschriften im Bericht: Serife, wie im Kundenbericht der Agentur. */
const KOPF = "font-serif font-semibold tracking-[-0.015em] text-[#1d1f26]";

const zahl = (n: unknown) =>
  typeof n === 'number' && Number.isFinite(n) ? new Intl.NumberFormat(locale(), { minimumFractionDigits: 1, maximumFractionDigits: 1 }).format(n) : '—';

const vorzeichen = (d: number) => `${d > 0 ? '+' : d < 0 ? '−' : '±'}${zahl(Math.abs(d))}`;

/**
 * Farben der Abstände: Blau, wo die geprüfte Fassung vorn liegt, Rost, wo die
 * Vergleichsfassung vorn liegt, Grau, wo es knapp ist. Blau gegen Rost statt
 * Grün gegen Rot: beide unterscheiden sich auch in der Helligkeit.
 */
const STUFE: Record<Stufe, string> = {
  'stark-minus': 'bg-[#1f6964] text-white',
  minus: 'bg-[#f6e3cf] text-[#6e3a12]',
  knapp: 'bg-[#e8eceb] text-[#565b62]',
  plus: 'bg-[#dcefed] text-[#163f3c]',
  'stark-plus': 'bg-[#1f6964] text-white',
};

/**
 * Eigene Typografie: ein Dokument liest sich anders als ein Kontoauszug.
 * Die Serife laedt nur diese Seite nach, der Rest der App braucht sie nicht.
 */
function Papier({ children, kopf, breit = false }: { children: React.ReactNode; kopf?: React.ReactNode; breit?: boolean }) {
  // Der Bericht als Vorschlag (S3) ist breiter: Hero mit zwei Bildern und Belege in drei Spalten, wie im Entwurf (1152 px Satzspiegel).
  const breite = breit ? 'max-w-[76rem]' : 'max-w-[64rem]';
  return (
    <div className="immer-hell min-h-dvh bg-[#f4f5f2] text-[#1d1f26] [-webkit-print-color-adjust:exact] [print-color-adjust:exact] print:min-h-0 print:bg-white">
      {/* A4 mit Rand. Im Tailwind-Stil gibt es fuer @page keine Klasse, und die
          Regel gilt nur, solange diese Seite offen ist. */}
      <style>{'@page { size: A4; margin: 16mm 15mm 18mm; } @media print { html, body { background: #fff; } }'}</style>
      {/* Das Logo in der hohen Aufloesung: der Bericht wird gedruckt und als PDF
          weitergegeben, und dort zaehlt jede Kante. 8 KB. */}
      <div className={`mx-auto flex ${breite} items-center justify-between gap-4 px-5 pt-6 font-mono text-[0.625rem] font-semibold tracking-[0.18em] text-[#62676e] uppercase sm:px-8 print:max-w-none print:px-0 print:pt-0`}>
        {/* Seit 13.10.2026 ein Link auf die Startseite (Robert): wer den Bericht
            weitergeleitet bekommt, soll mit einem Klick sehen, was 99takes ist.
            Höhe 44 px als Tippfläche, das Bild bleibt 24 px hoch. */}
        <a href="https://www.99takes.com" className="-my-2.5 flex min-h-11 items-center rounded-sm focus-visible:outline-3 focus-visible:outline-offset-2 focus-visible:outline-[#1f6964]">
          <img src="/logo-112.png" width={381} height={112} alt={t('99takes, zur Startseite', '99takes, to the home page')} className="block h-6 w-auto print:h-[7mm]" />
        </a>
        <span className="flex min-w-0 items-center gap-4">
          {kopf && <span className="hidden min-w-0 truncate text-right sm:block print:block">{kopf}</span>}
          {/* Sprachumschalter (seit 16.10.2026): klein, rechts oben, nie im Druck. */}
          <SprachWahl className="shrink-0 font-sans tracking-normal normal-case print:hidden" />
        </span>
      </div>
      <main className={`mx-auto ${breite} px-5 pt-10 pb-20 text-[1rem] leading-[1.6] sm:px-8 sm:pt-14 print:max-w-none print:px-0 print:pt-6 print:pb-0 print:text-[10.5pt]`}>{children}</main>
    </div>
  );
}

function Augenbraue({ children }: { children: React.ReactNode }) {
  return (
    <p className="mt-0 mb-3 flex w-fit break-after-avoid items-center gap-3 font-mono text-[0.6875rem] font-bold tracking-[0.22em] text-[#1f6964] uppercase">
      <span aria-hidden className="h-px w-6 bg-[#3f8a87]" />
      {children}
    </p>
  );
}

/** Eine beschriftete Zeile in einer `<dl>` mit zwei Spalten ab `sm`. */
function Angabe({ titel, children }: { titel: string; children: React.ReactNode }) {
  return (
    <>
      <dt className="pt-[0.3em] font-mono text-[0.625rem] font-bold tracking-[0.16em] text-[#5f646b] uppercase">{titel}</dt>
      <dd className="m-0 mb-2 text-[#2f3238] [overflow-wrap:anywhere] sm:mb-0">{children}</dd>
    </>
  );
}

/** Ein Zitat in deutschen Anführungszeichen; Anführungszeichen darin werden einfache. Englisch: “…” und ‘…’. */
const zitiert = (x: unknown) => sprache() === 'en'
  ? `“${String(x ?? '').trim().replace(/[„“"]([^„“”"]*)[“”"]/g, '‘$1’')}”`
  : `„${String(x ?? '').trim().replace(/[„“]([^„“”"]*)[“”"]/g, '‚$1‘')}“`;

/** Kleine Zahlen in Überschriften als Wort: „drei Kernpersonas“, nicht „3 Kernpersonas“ am Satzanfang. */
const anzahlWort = (n: number) => t(
  ['null', 'eine', 'zwei', 'drei', 'vier', 'fünf', 'sechs', 'sieben', 'acht', 'neun', 'zehn', 'elf', 'zwölf'],
  ['zero', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten', 'eleven', 'twelve'],
)[n] ?? String(n);

/** Das Gerät aus den Stufen der Gestaltung (bericht-blatt.ts) als Wort der Seite. */
const geraetName = (g: string) => (g === 'Handy' ? t('Handy', 'Mobile') : g);

/**
 * Das Ergebnis als Satz mit Zahl, wie im Kundenbericht: „… gewinnt 5 : 1".
 * Die Zahl sind Personas, nicht Punkte; bei einem Lauf ohne Vergleich steht
 * dort, wie viele weitergehen wuerden. Seit 09.10.2026 die Überschrift, wenn
 * der Autor kein Urteil, keine Kernaussage und keine kurze Zusammenfassung
 * geschrieben hat (ältere Berichte).
 */
function ErgebnisSatz({ v }: { v: any }) {
  const dafuer = v.wins?.candidate;
  const gesamt = v.wins?.total;
  const dagegen = typeof dafuer === 'number' && typeof gesamt === 'number' ? gesamt - dafuer : null;
  const name = v.label ? t(`„${fassungsname(v.label)}“`, `“${fassungsname(v.label)}”`) : t('Die geprüfte Fassung', 'The tested version');
  const hl = 'text-[#3f8a87] italic';
  // Mit Kern und Rand zaehlen `wins` nur den Kern (report.ts); der Satz sagt das.
  const imKern = v.mode !== 'feedback' && leseKernRand(v) ? t(' im Kern', ' in the core') : '';
  let satz: React.ReactNode;
  if (v.mode === 'feedback') {
    satz = t(
      <><span className={hl}>{dafuer ?? '—'} von {gesamt ?? '—'}</span> Personas würden den nächsten Schritt gehen.</>,
      <><span className={hl}>{dafuer ?? '—'} of {gesamt ?? '—'}</span> personas would take the next step.</>,
    );
  } else if (v.verdict === 'candidate') {
    satz = t(
      <>{name} gewinnt{imKern} <span className={`${hl} whitespace-nowrap tabular-nums`}>{dafuer} : {dagegen}</span>.</>,
      <>{name} wins{imKern} <span className={`${hl} whitespace-nowrap tabular-nums`}>{dafuer} : {dagegen}</span>.</>,
    );
  } else if (v.verdict === 'baseline') {
    satz = t(
      <>Die Vergleichsfassung liegt{imKern} vorn, <span className={`${hl} whitespace-nowrap tabular-nums`}>{dagegen} : {dafuer}</span> gegen {name}.</>,
      <>The baseline leads{imKern}, <span className={`${hl} whitespace-nowrap tabular-nums`}>{dagegen} : {dafuer}</span> against {name}.</>,
    );
  } else {
    satz = t(
      <>Kein klares Urteil{imKern}: <span className={`${hl} whitespace-nowrap tabular-nums`}>{dafuer} : {dagegen}</span> für {name}.</>,
      <>No clear verdict{imKern}: <span className={`${hl} whitespace-nowrap tabular-nums`}>{dafuer} : {dagegen}</span> for {name}.</>,
    );
  }
  return <>{satz}</>;
}

/**
 * „ Urteilen“, wenn Personas mehrmals urteilten (Matrix): dann zählt `wins`
 * Urteile, nicht Personas. Sonst leer, wie vorher.
 */
function urteilEinheit(x: any): string {
  return anzahlUrteile(x) > personaNoten(x).length ? t(' Urteilen', ' verdicts') : '';
}

/** Durchschnittspunkte als Balken, beschriftet. Ohne Zahl kein Balken. */
function Punktbalken({ label, wert, stark = false }: { label: string; wert: unknown; stark?: boolean }) {
  const n = typeof wert === 'number' && Number.isFinite(wert) ? wert : null;
  return (
    <div className="grid grid-cols-[minmax(0,8.5rem)_1fr_2.5rem] items-center gap-3 py-1 text-[0.8125rem] sm:grid-cols-[10rem_1fr_3rem]">
      <span className={stark ? 'font-semibold text-[#1d1f26]' : 'text-[#5f646b]'}>{label}</span>
      <span className="h-2 overflow-hidden rounded-full bg-[#e3e8e6]" aria-hidden>
        {n != null && <span className={`block h-full rounded-full ${stark ? 'bg-[#1f6964]' : 'bg-[#6c4f80]'}`} style={{ width: `${Math.min(100, Math.max(0, n * 10))}%` }} />}
      </span>
      <span className={`text-right tabular-nums ${stark ? 'font-semibold text-[#1d1f26]' : 'text-[#5f646b]'}`}>{zahl(n)}</span>
    </div>
  );
}

function UrteilEtikett({ v }: { v: any }) {
  if (v.mode === 'feedback' || !v.verdict) return <span className="rounded-full bg-[#1d1f26]/[0.07] px-2.5 py-0.5 text-[0.75rem] font-semibold whitespace-nowrap text-[#5f646b]">{t('ohne Vergleich', 'no comparison')}</span>;
  const f = v.verdict === 'candidate' ? STUFE.plus : v.verdict === 'baseline' ? STUFE.minus : STUFE.knapp;
  return <span className={`rounded-full px-2.5 py-0.5 text-[0.75rem] font-semibold whitespace-nowrap ${f}`}>{urteilWort(v.verdict) ?? v.verdict}</span>;
}

/**
 * Verlauf über mehrere Fassungen: je Fassung beide Kern-Schnitte auf einer
 * gemeinsamen Skala, damit man sieht, ob der Abstand kleiner wird. Die Skala
 * reicht von der niedrigsten zur höchsten Note, auf ganze Punkte gerundet.
 */
function Verlauf({ varianten }: { varianten: any[] }) {
  const noten = varianten.flatMap((x) => (x.mode === 'feedback' ? [] : [x.scores?.candidate, x.scores?.baseline]))
    .filter((v): v is number => typeof v === 'number' && Number.isFinite(v));
  const von = noten.length ? Math.max(0, Math.floor(Math.min(...noten)) - 1) : 0;
  const bis = noten.length ? Math.min(10, Math.ceil(Math.max(...noten)) + 1) : 10;
  const pos = (s: number) => `${((s - von) / Math.max(1, bis - von)) * 100}%`;
  return (
    <div className="overflow-hidden rounded-xl border border-[#dde3e1] bg-[#ffffff]">
      <div className="flex flex-wrap items-center justify-between gap-x-5 gap-y-1 border-b border-[#e8eceb] px-5 py-3 text-[0.8125rem] text-[#565b62]">
        <span className="font-semibold text-[#1d1f26]">{t('Verlauf der Fassungen', 'Progression of versions')}</span>
        <span className="flex flex-wrap gap-4">
          <span className="inline-flex items-center gap-1.5"><span className="size-3 rounded-full bg-[#1f6964]" aria-hidden />{t('geprüfte Fassung', 'tested version')}</span>
          <span className="inline-flex items-center gap-1.5"><span className="size-3 rounded-full border-2 border-[#6c4f80] bg-[#ffffff]" aria-hidden />{t('Vergleichsfassung', 'baseline')}</span>
        </span>
      </div>
      {varianten.map((x: any, i: number) => {
        const c = x.scores?.candidate, b = x.scores?.baseline;
        const hat = x.mode !== 'feedback' && typeof c === 'number' && typeof b === 'number';
        return (
          <div key={x.run_id ?? i} className="grid items-center gap-x-6 gap-y-2 border-b border-[#e8eceb] px-5 py-4 last:border-b-0 sm:grid-cols-[minmax(0,14rem)_minmax(0,1fr)_auto]">
            <span className="flex min-w-0 items-baseline gap-2">
              <span className="font-mono text-[0.75rem] text-[#5f646b]">F{i + 1}</span>
              <a href={`#fassung-${i + 1}`} className="font-medium text-[#1d1f26] underline decoration-[#b7c0be] underline-offset-[3px] hover:text-[#1f6964] [overflow-wrap:anywhere]">{fassungsname(x.label)}</a>
            </span>
            {hat ? (
              <span className="flex flex-col gap-1">
                <span className="relative h-6" aria-hidden>
                  <span className="absolute inset-x-0 top-[11px] h-0.5 bg-[#e8eceb]" />
                  <span className="absolute top-[10px] h-1 rounded bg-[#b7c0be]" style={{ left: pos(Math.min(c, b)), width: `calc(${pos(Math.max(c, b))} - ${pos(Math.min(c, b))})` }} />
                  <span className="absolute top-1 -ml-2 size-4 rounded-full border-[3px] border-[#6c4f80] bg-[#ffffff]" style={{ left: pos(b) }} />
                  <span className="absolute top-1 -ml-2 size-4 rounded-full bg-[#1f6964]" style={{ left: pos(c) }} />
                </span>
                <span className="flex justify-between text-[0.8125rem] text-[#565b62] tabular-nums">
                  <span><strong className="text-[#1f6964]">{zahl(c)}</strong> {t('zu', 'to')} <strong className="text-[#6c4f80]">{zahl(b)}</strong></span>
                  <span>{x.wins?.candidate ?? '—'} {t('von', 'of')} {x.wins?.total ?? '—'}{urteilEinheit(x)}{leseKernRand(x) ? t(' im Kern', ' in the core') : ''} {t('dafür', 'in favor')}</span>
                </span>
              </span>
            ) : (
              <span className="text-[0.8125rem] text-[#565b62]">{x.wins?.candidate ?? '—'} {t('von', 'of')} {x.wins?.total ?? '—'} {t('würden weitergehen', 'would go on')}</span>
            )}
            <span className="justify-self-start sm:justify-self-end"><UrteilEtikett v={x} /></span>
          </div>
        );
      })}
      <p className="m-0 border-t border-[#e8eceb] px-5 py-2 text-[0.75rem] text-[#5f646b]">
        {t(
          <>{leseKernRand(varianten[varianten.length - 1]) ? 'Schnitt über die Kernpersonas' : 'Schnitt über alle Personas'}, Skala {von} bis {bis} von 10.</>,
          <>{leseKernRand(varianten[varianten.length - 1]) ? 'Average over the core personas' : 'Average over all personas'}, scale {von} to {bis} out of 10.</>,
        )}
      </p>
    </div>
  );
}

/** Persona × Fassung: je Zelle der Abstand zur Vergleichsfassung, beide Noten im Tooltip. */
function PersonaMatrix({ m, kurz }: { m: Matrix; kurz: (label: string) => string }) {
  const ersteRand = m.zeilen.findIndex((z) => z.rand);
  // Bis vier Fassungen passt die Tabelle auch auf 340 px (Roberts Testtelefon),
  // ohne seitlich zu scrollen; darüber scrollt sie im Kasten.
  const eng = m.spalten.length <= 4;
  return (
    <>
      <div className="mb-3 flex flex-wrap gap-1.5 text-[0.75rem]" aria-label={t('Legende', 'Legend')}>
        <span className={`rounded px-2 py-0.5 ${STUFE['stark-minus']}`}>{t('−1 und mehr', '−1 and more')}</span>
        <span className={`rounded px-2 py-0.5 ${STUFE.minus}`}>{t('−0,3 bis −1', '−0.3 to −1')}</span>
        <span className={`rounded px-2 py-0.5 ${STUFE.knapp}`}>{t('knapp', 'close')}</span>
        <span className={`rounded px-2 py-0.5 ${STUFE.plus}`}>{t('+0,3 bis +1', '+0.3 to +1')}</span>
        <span className={`rounded px-2 py-0.5 ${STUFE['stark-plus']}`}>{t('+1 und mehr', '+1 and more')}</span>
      </div>
      <div className="overflow-x-auto rounded-xl border border-[#dde3e1] bg-[#ffffff]">
        <table className={`w-full border-collapse text-[0.8125rem] sm:text-[0.875rem] ${eng ? '' : 'min-w-[34rem]'}`}>
          <thead>
            <tr className="bg-[#eef2f1] text-left">
              <th scope="col" className="px-3 py-2.5 font-semibold text-[#2f3238] sm:px-4">Persona</th>
              {m.spalten.map((s) => (
                <th key={s.kurz} scope="col" title={s.name} className="w-12 px-0.5 py-2.5 text-center font-semibold text-[#2f3238] sm:w-24 sm:px-2">{s.kurz}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {m.zeilen.map((z, i) => (
              <tr key={z.persona} className={`border-t ${i === ersteRand && i > 0 ? 'border-[#b7c0be]' : 'border-[#e8eceb]'} break-inside-avoid`}>
                <th scope="row" title={z.persona} className={`px-3 py-2 text-left leading-snug font-medium sm:px-4 sm:py-2.5 ${z.rand ? 'text-[#565b62]' : 'text-[#1d1f26]'} [overflow-wrap:anywhere]`}>
                  {kurz(z.persona)}{z.rand && <RandMarke />}
                </th>
                {z.zellen.map((c, j) => (
                  <td key={j} className="px-0.5 py-1.5 text-center sm:px-1.5">
                    {c ? (
                      <span title={`${zahl(c.geprueft)} ${t('zu', 'to')} ${zahl(c.vergleich)}`} className={`inline-block min-w-11 rounded-md px-1 py-1.5 text-[0.75rem] font-semibold tabular-nums sm:min-w-14 sm:px-2 sm:text-[0.8125rem] ${STUFE[stufe(c.delta)]}`}>
                        {vorzeichen(c.delta)}
                      </span>
                    ) : <span className="text-[#bcc5c3]">–</span>}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="mt-2 mb-0 text-[0.8125rem] leading-relaxed text-[#5f646b]">
        {t('Zahl = Note der Fassung minus Note der Vergleichsfassung.', 'Number = score of the version minus score of the baseline.')}
        {' '}{m.spalten.map((s) => `${s.kurz}: ${s.name}`).join(' · ')}
      </p>
    </>
  );
}

/** Ein Muster in „Was zieht" oder „Was bremst": Titel, Anteil der Personas als Balken, wer es nennt. */
function Muster({ titel, text, personas, gesamt, farbe, offenIn, nurRand = false, zitat = false, stimme }: {
  titel: string; text?: string; personas: string[]; gesamt: number; farbe: 'blau' | 'rost'; offenIn?: string[]; nurRand?: boolean; zitat?: boolean;
  /** Ein Satz einer Persona im Wortlaut, vom Server aus dem Run gewählt (report.ts, zitateFuerHebel). */
  stimme?: { text: string; persona: string };
}) {
  const anteil = gesamt > 0 ? Math.min(100, (personas.length / gesamt) * 100) : 0;
  return (
    <li className="flex flex-col gap-2 rounded-xl border border-[#dde3e1] bg-[#ffffff] px-5 py-4 break-inside-avoid">
      <div className="flex items-baseline justify-between gap-4">
        {/* Ein Einwand ist die Stimme einer Persona und steht als Zitat; fett
            gesetzt las sich die Spalte wie fünf Überschriften (08.10.2026). */}
        {zitat
          ? <span className="text-[1.0625rem] leading-snug text-[#1d1f26] italic font-serif">{zitiert(titel)}</span>
          : <span className="font-semibold leading-snug text-[#1d1f26]">{titel}</span>}
        {personas.length > 0 && (
          <span className={`shrink-0 font-mono text-[0.75rem] font-bold ${farbe === 'blau' ? 'text-[#1f6964]' : 'text-[#8a4a17]'}`}>
            {personas.length}{gesamt ? t(` von ${gesamt} Personas`, ` of ${gesamt} personas`) : ''}
          </span>
        )}
      </div>
      {personas.length > 0 && gesamt > 0 && (
        <span className="h-1.5 overflow-hidden rounded-full bg-[#e8eceb]" aria-hidden>
          <span className={`block h-full rounded-full ${farbe === 'blau' ? 'bg-[#1f6964]' : 'bg-[#d07a3e]'}`} style={{ width: `${anteil}%` }} />
        </span>
      )}
      {text && <p className="m-0 text-[0.9375rem] leading-relaxed text-[#2f3238]">{text}</p>}
      {stimme && (
        <p className="m-0 text-[1rem] leading-snug text-[#1d1f26] italic font-serif">
          {zitiert(stimme.text)}{stimme.persona && <span className="text-[0.8125rem] text-[#5f646b] not-italic"> ({stimme.persona})</span>}
        </p>
      )}
      <p className="m-0 text-[0.8125rem] text-[#5f646b]">
        {personas.join(' · ')}
        {nurRand && t(' (nur erweiterte Zielgruppe)', ' (extended audience only)')}
        {offenIn && offenIn.length > 0 && <> · in {offenIn.join(', ')}</>}
      </p>
    </li>
  );
}

const BEREICH: Record<string, string> = {
  hierarchie: 'Hierarchie', typografie: 'Typografie', layout: 'Layout', farbe: 'Farbe', bedienung: 'Bedienelemente',
  barrierefreiheit: 'Barrierefreiheit', einheitlichkeit: 'Einheitlichkeit', handy: 'Handy', politur: 'Politur',
};
const BEREICH_EN: Record<string, string> = {
  hierarchie: 'Hierarchy', typografie: 'Typography', layout: 'Layout', farbe: 'Color', bedienung: 'Controls',
  barrierefreiheit: 'Accessibility', einheitlichkeit: 'Consistency', handy: 'Mobile', politur: 'Polish',
};
const bereichName = (b: string) => t(BEREICH, BEREICH_EN)[b] ?? b;

/**
 * Ein Design-Check: Bildschirmaufnahmen (neu signiert bei jedem Abruf), Noten
 * je Kriterium mit der schwächsten Stelle markiert, Unverwechselbarkeit,
 * Befunde des Fachprüfers ab Grad 2. Ältere Berichte haben das Feld nicht.
 */
function DesignCheck({ d, fassung, ebene = 3 }: { d: any; fassung?: FassungRef; ebene?: 3 | 5 }) {
  const H = ebene === 5 ? 'h5' : 'h3';
  const url = sichererLink(d.url);
  const noten = Object.entries(d.noten ?? {}) as [string, number][];
  return (
    <article className="break-inside-avoid">
      <p className="mt-0 mb-3 text-[0.875rem] text-[#5f646b]">
        {d.quelle ? <>{d.quelle === 'html' ? t('Entwurf (HTML)', 'Draft (HTML)') : t('Entwurf (Bilder)', 'Draft (images)')}{url ? t(' für ', ' for ') : ''}</> : null}
        {url ? <a className="text-[#1f6964] underline underline-offset-2 [overflow-wrap:anywhere]" href={url} rel="noopener noreferrer nofollow">{d.quelle ? String(d.url).replace(/#.*$/, '') : d.url}</a> : d.quelle ? null : d.url}
        {' · '}{tag(d.am)}{d.personas ? t(` · ${d.personas} Personas`, ` · ${d.personas} personas`) : ''}
      </p>
      {d.skizze && <SkizzeHinweis sk={d.skizze} fassung={fassung} />}
      <Bilder d={d} />
      <div className="grid gap-6 md:grid-cols-2">
        {noten.length > 0 && (
          <div>
            <H className="mt-0 mb-2 font-mono text-[0.6875rem] font-bold tracking-[0.18em] text-[#5f646b] uppercase">{t('Noten der Personas', 'Persona scores')}</H>
            {noten.map(([k, v]) => (
              <Punktbalken key={k} label={`${KRITERIUM[k] ?? k}${d.schwaechste === k ? t(' (schwächste)', ' (weakest)') : ''}`} wert={v} stark={d.schwaechste === k} />
            ))}
            {d.wettbewerb && (
              <p className="mt-3 mb-0 text-[0.875rem] leading-relaxed text-[#2f3238]">
                {t(<>
                  Unverwechselbarkeit gegen {d.wettbewerb.von ? 'die Wettbewerber' : 'den Wettbewerb'}: <strong className="tabular-nums">{zahl(d.wettbewerb.unverwechselbarkeit)}</strong> von 10
                  {d.wettbewerb.von ? <>, {d.wettbewerb.zuerst_gemerkt} von {d.wettbewerb.von} Personas würden sich diese Seite zuerst merken</> : null}.
                </>, <>
                  Distinctiveness against {d.wettbewerb.von ? 'the competitors' : 'the competition'}: <strong className="tabular-nums">{zahl(d.wettbewerb.unverwechselbarkeit)}</strong> out of 10
                  {d.wettbewerb.von ? <>, {d.wettbewerb.zuerst_gemerkt} of {d.wettbewerb.von} personas would remember this page first</> : null}.
                </>)}
              </p>
            )}
          </div>
        )}
        {d.handwerk && <Fachpruefer h={d.handwerk} skizze={!!d.skizze} ebene={ebene} />}
      </div>
      {Array.isArray(d.vorschlaege) && d.vorschlaege.length > 0 && <Vorschlaege liste={d.vorschlaege} ebene={ebene} />}
    </article>
  );
}

/**
 * Vorschläge zum Umbau (seit 09.10.2026, vorschlaege.ts): je Vorschlag Titel,
 * Wireframe von oben nach unten, warum und was dafür rausfällt. Ältere
 * Berichte haben das Feld nicht.
 */
function Vorschlaege({ liste, ebene = 3 }: { liste: any[]; ebene?: 3 | 5 }) {
  const H = ebene === 5 ? 'h5' : 'h3';
  return (
    <div className="mt-8">
      <H className="mt-0 mb-3 font-mono text-[0.6875rem] font-bold tracking-[0.18em] text-[#5f646b] uppercase">{t('Vorschläge zum Umbau', 'Redesign suggestions')}</H>
      <ol className="m-0 grid list-none gap-4 p-0 md:grid-cols-2">
        {liste.map((v: any, i: number) => (
          <li key={i} className="break-inside-avoid rounded-lg border border-[#dde3e1] bg-[#ffffff] px-4 py-3.5 text-[0.875rem] leading-relaxed text-[#2f3238]">
            <p className="mt-0 mb-1 font-mono text-[0.625rem] font-bold tracking-[0.16em] text-[#5f646b] uppercase">
              {v.bereich === 'erster_bildschirm' ? t('Above the Fold', 'Above the fold') : t('Schwächste Stelle', 'Weakest spot')}{v.muster ? ` · ${v.muster}` : ''}
            </p>
            <p className="mt-0 mb-2 font-semibold text-[#1d1f26]">{i + 1}. {v.titel}</p>
            <ul className="mt-0 mb-2 list-none space-y-1 rounded-md border border-dashed border-[#cfd6d4] bg-white p-2.5 font-mono text-[0.75rem] text-[#2f3238]">
              {(v.wireframe ?? []).map((z: string, j: number) => <li key={j} className="[overflow-wrap:anywhere]">{z}</li>)}
            </ul>
            {v.warum && <p className="mt-0 mb-1"><span className="font-medium text-[#1d1f26]">{t('Warum: ', 'Why: ')}</span>{v.warum}</p>}
            {v.raus && <p className="m-0"><span className="font-medium text-[#1d1f26]">{t('Dafür raus: ', 'Out in return: ')}</span>{v.raus}</p>}
          </li>
        ))}
      </ol>
      <p className="mt-3 mb-0 text-[0.8125rem] text-[#5f646b]">{t('Vorschläge eines Prüfers, keine Messung. Ob ein Umbau besser wirkt, zeigt erst ein Check des Umbaus.', 'Suggestions from a reviewer, not a measurement. Whether a redesign works better only shows in a check of the redesign.')}</p>
    </div>
  );
}

function SkizzeHinweis({ sk, fassung }: { sk: any; fassung?: FassungRef }) {
  return (
    <p className="mt-0 mb-4 rounded-lg border border-[#dde3e1] bg-[#eef2f1] px-4 py-3 text-[0.875rem] leading-relaxed text-[#2f3238]">
      {t(<>
        <strong>Skizze:</strong> {fassung ? <>F{fassung.nr} „{fassung.label}“</> : 'eine neue Textfassung'} in die heutige Seite gesetzt
        {sk.abdeckung != null ? <>, {Math.round(sk.abdeckung * 100)} % ihres Texts stehen auf der Seite</> : null}.
        Keine Gestaltung: Neue Abschnitte sind Kopien vorhandener, Bilder und Tabellen bleiben, wie sie sind.
      </>, <>
        <strong>Sketch:</strong> {fassung ? <>F{fassung.nr} “{fassung.label}”</> : 'a new text version'} put into today's page
        {sk.abdeckung != null ? <>, {Math.round(sk.abdeckung * 100)}% of its text appears on the page</> : null}.
        No design: new sections are copies of existing ones, images and tables stay as they are.
      </>)}
    </p>
  );
}

function Bilder({ d, klein = false }: { d: any; klein?: boolean }) {
  const links = d.bild_links ?? {};
  if (!links.desktop && !links.mobil) return null;
  return (
    <div className={`mb-5 grid gap-3 ${klein ? 'grid-cols-[minmax(0,3fr)_minmax(0,1fr)]' : 'sm:grid-cols-[minmax(0,3fr)_minmax(0,1fr)]'}`}>
      {(['desktop', 'mobil'] as const).filter((a) => links[a]).map((a) => (
        <figure key={a} className="m-0">
          <figcaption className="mb-1 font-mono text-[0.625rem] font-bold tracking-[0.16em] text-[#5f646b] uppercase">{a === 'desktop' ? 'Desktop' : t('Handy', 'Mobile')}</figcaption>
          <a href={links[a]} target="_blank" rel="noopener noreferrer" className="block h-80 overflow-y-auto rounded-lg border border-[#dde3e1] bg-white print:h-auto print:max-h-[120mm] print:overflow-hidden" title={t('Ganze Seite öffnen', 'Open full page')}>
            <img src={links[a]} alt={t(
              `${d.skizze ? 'Skizze der Fassung' : 'Die Seite'} ${a === 'desktop' ? 'am Desktop' : 'am Handy'}, wie 99takes sie gesehen hat`,
              `${d.skizze ? 'Sketch of the version' : 'The page'} ${a === 'desktop' ? 'on desktop' : 'on mobile'}, as 99takes saw it`,
            )} className="block w-full" />
          </a>
        </figure>
      ))}
    </div>
  );
}

/**
 * Fachprüfer: Stärken, Grad 3 und 4 sichtbar, der Rest aufgeklappt (seit
 * 08.10.2026: zwölf Befunde mit „Tun" las kein Kunde). Bei einer Skizze der
 * Hinweis, dass Abstände und fehlende Titel vom Einsetzen kommen können.
 */
function Fachpruefer({ h, skizze, ebene = 3 }: { h: any; skizze: boolean; ebene?: 3 | 5 }) {
  const H = ebene === 5 ? 'h5' : 'h3';
  const { zuerst, weitere } = teileBefunde(h.befunde ?? []);
  const befund = (b: any, i: number) => (
    <li key={i} className="rounded-lg border border-[#dde3e1] bg-[#ffffff] px-3.5 py-2.5 text-[0.875rem]">
      <span className={`mr-2 rounded px-1.5 py-0.5 text-[0.6875rem] font-semibold ${b.schwere >= 3 ? STUFE['stark-minus'] : STUFE.minus}`}>{t('Grad', 'Grade')} {b.schwere}</span>
      <span className="font-medium text-[#1d1f26]">{bereichName(b.bereich)}:</span> {b.was}
      {b.tun && <span className="mt-1 block text-[#565b62]">{t('Tun', 'To do')}: {b.tun}</span>}
    </li>
  );
  const rest = weitere.length + (Number(h.kleinere) || 0);
  return (
    <div>
      <H className="mt-0 mb-2 font-mono text-[0.6875rem] font-bold tracking-[0.18em] text-[#5f646b] uppercase">{t('Fachprüfer', 'Expert reviewer')}</H>
      {h.staerken?.length > 0 && (
        <ul className="mt-0 mb-3 flex list-disc flex-col gap-1 pl-5 text-[0.875rem] leading-relaxed text-[#2f3238] marker:text-[#3f8a87]">
          {h.staerken.slice(0, 2).map((s: string, i: number) => <li key={i}>{s}</li>)}
        </ul>
      )}
      {zuerst.length > 0 ? <ul className="m-0 list-none space-y-2 p-0">{zuerst.map(befund)}</ul>
        : <p className="m-0 text-[0.875rem] text-[#5f646b]">{t('Kein Befund ab Grad 2.', 'No finding of grade 2 or higher.')}</p>}
      {weitere.length > 0 && (
        <details className="mt-3 print:hidden">
          <summary className="flex min-h-11 cursor-pointer items-center text-[0.8125rem] text-[#1f6964]">{t(`${weitere.length === 1 ? 'Einen weiteren Befund' : `${weitere.length} weitere Befunde`} mit Grad 2 zeigen`, `Show ${weitere.length === 1 ? 'one more finding' : `${weitere.length} more findings`} with grade 2`)}</summary>
          <ul className="m-0 mt-2 list-none space-y-2 p-0">{weitere.map(befund)}</ul>
        </details>
      )}
      {rest > 0 && (
        <p className="mt-2 mb-0 text-[0.8125rem] text-[#5f646b]">
          {t(<>
            {weitere.length > 0 ? 'Insgesamt ' : 'Dazu '}{rest} weitere {rest === 1 ? 'Befund' : 'Befunde'}
            {Number(h.kleinere) > 0 ? `, davon ${h.kleinere} Feinschliff (Grad 0 bis 1)` : ''}.
          </>, <>
            {weitere.length > 0 ? 'In total ' : 'Plus '}{rest} more {rest === 1 ? 'finding' : 'findings'}
            {Number(h.kleinere) > 0 ? `, of which ${h.kleinere} polish (grade 0 to 1)` : ''}.
          </>)}
        </p>
      )}
      {skizze && (
        <p className="mt-2 mb-0 text-[0.8125rem] leading-relaxed text-[#5f646b]">
          {t('Bei einer Skizze können Befunde zu Abständen, fehlenden Titeln oder losem Text unter Karten vom Einsetzen kommen, nicht von der Gestaltung.',
            'With a sketch, findings about spacing, missing titles or loose text under cards can come from inserting the text, not from the design.')}
        </p>
      )}
    </div>
  );
}

/**
 * Heute gegen die Skizze derselben Seite: ein Satz, die Bilder nebeneinander,
 * die Noten als Tabelle, dann die Prüferbefunde je Bereich in einer Zeile
 * (seit 08.10.2026; vorher je Seite eine lange Spalte, und am Handy standen
 * beide Listen untereinander).
 */
function VorherNachher({ heute, skizze, fassung }: { heute: any; skizze: any; fassung?: FassungRef }) {
  const v = vergleicheNoten(heute, skizze);
  const url = sichererLink(heute.url);
  // Dativ, weil es immer „mit …“ heißt.
  const name = fassung ? `F${fassung.nr}` : t('der neuen Fassung', 'the new version');
  const vorz = (n: number) => `${n > 0 ? '+' : n < 0 ? '−' : '±'}${zahl(Math.abs(n))}`;
  return (
    <article>
      <p className="mt-0 mb-3 text-[0.875rem] text-[#5f646b]">
        {url ? <a className="text-[#1f6964] underline underline-offset-2 [overflow-wrap:anywhere]" href={url} rel="noopener noreferrer nofollow">{heute.url}</a> : heute.url}
        {' · '}{t(
          <>heute geprüft am {tag(heute.am)}, Skizze am {tag(skizze.am)}{skizze.personas ? ` · je ${skizze.personas} Personas` : ''}</>,
          <>today checked on {tag(heute.am)}, sketch on {tag(skizze.am)}{skizze.personas ? ` · ${skizze.personas} personas each` : ''}</>,
        )}
      </p>
      <SkizzeHinweis sk={skizze.skizze} fassung={fassung} />
      {v.mittel != null && (
        <p className={`${KOPF} mt-0 mb-6 max-w-[60ch] text-[1.25rem] leading-snug`}>
          {t(<>
            Mit {name} in der heutigen Gestaltung {v.mittel < -0.2 ? 'sinken' : v.mittel > 0.2 ? 'steigen' : 'bleiben'} die Noten
            {Math.abs(v.mittel) > 0.2 ? <> im Mittel um <span className="tabular-nums">{zahl(Math.abs(v.mittel))}</span> Punkte</> : ' etwa gleich'}
            {v.deutlichste && Math.abs(v.deutlichste.abstand ?? 0) >= 0.5
              ? <>, am deutlichsten bei „{KRITERIUM[v.deutlichste.kriterium] ?? v.deutlichste.kriterium}“ ({zahl(v.deutlichste.heute)} → {zahl(v.deutlichste.neu)})</>
              : null}.
          </>, <>
            With {name} in today's design, the scores {v.mittel < -0.2 ? 'fall' : v.mittel > 0.2 ? 'rise' : 'stay'}
            {Math.abs(v.mittel) > 0.2 ? <> by <span className="tabular-nums">{zahl(Math.abs(v.mittel))}</span> points on average</> : ' about the same'}
            {v.deutlichste && Math.abs(v.deutlichste.abstand ?? 0) >= 0.5
              ? <>, most clearly for “{KRITERIUM[v.deutlichste.kriterium] ?? v.deutlichste.kriterium}” ({zahl(v.deutlichste.heute)} → {zahl(v.deutlichste.neu)})</>
              : null}.
          </>)}
        </p>
      )}
      <div className="mb-6 grid gap-6 md:grid-cols-2 print:grid-cols-2">
        {[{ titel: t('Heute', 'Today'), d: heute }, { titel: t(`Mit ${name} (Skizze)`, `With ${name} (sketch)`), d: skizze }].map(({ titel, d }) => (
          <section key={titel} className="min-w-0">
            <h3 className={`${KOPF} mt-0 mb-2 text-[1.125rem]`}>{titel}</h3>
            <Bilder d={d} klein />
          </section>
        ))}
      </div>
      <div className="mb-8 overflow-hidden rounded-xl border border-[#dde3e1] bg-[#ffffff] break-inside-avoid">
        <table className="w-full border-collapse text-[0.875rem]">
          <thead>
            <tr className="border-b border-[#dde3e1] text-left text-[0.75rem] text-[#5f646b]">
              <th className="px-3 py-2 font-medium sm:px-4">{t('Noten der Personas', 'Persona scores')}</th>
              <th className="px-1.5 py-2 text-right font-medium sm:px-3">{t('Heute', 'Today')}</th>
              <th className="py-2 pr-3 pl-1.5 text-right font-medium sm:px-3">{t('Mit', 'With')} {name}</th>
              <th className="hidden px-3 py-2 text-right font-medium sm:table-cell sm:px-4">{t('Abstand', 'Gap')}</th>
            </tr>
          </thead>
          <tbody>
            {v.zeilen.map((z) => (
              <tr key={z.kriterium} className="border-b border-[#e8eceb] last:border-0">
                <td className="px-3 py-2 text-[#1d1f26] sm:px-4">{KRITERIUM[z.kriterium] ?? z.kriterium}</td>
                <td className="px-1.5 py-2 text-right tabular-nums sm:px-3">{zahl(z.heute)}</td>
                <td className="py-2 pr-3 pl-1.5 text-right tabular-nums sm:px-3">
                  {zahl(z.neu)}
                  {/* Am Handy (340 px) fehlt der Platz für eine eigene Spalte. */}
                  {z.abstand != null && <span className={`mt-0.5 block justify-self-end rounded px-1 text-[0.6875rem] font-semibold sm:hidden ${STUFE[stufe(z.abstand)]}`}>{vorz(z.abstand)}</span>}
                </td>
                <td className="hidden px-3 py-2 text-right sm:table-cell sm:px-4">
                  {z.abstand != null && <span className={`rounded px-1.5 py-0.5 text-[0.75rem] font-semibold tabular-nums ${STUFE[stufe(z.abstand)]}`}>{vorz(z.abstand)}</span>}
                </td>
              </tr>
            ))}
            {(heute.wettbewerb || skizze.wettbewerb) && (
              <tr className="border-t border-[#dde3e1]">
                <td className="px-3 py-2 text-[#565b62] [overflow-wrap:anywhere] sm:px-4">{t('Unverwechselbarkeit gegen die Wettbewerber', 'Distinctiveness against the competitors')}</td>
                <td className="px-1.5 py-2 text-right tabular-nums sm:px-3">{heute.wettbewerb ? zahl(heute.wettbewerb.unverwechselbarkeit) : <span className="text-[#5f646b]">–</span>}</td>
                <td className="py-2 pr-3 pl-1.5 text-right tabular-nums sm:px-3">{skizze.wettbewerb ? zahl(skizze.wettbewerb.unverwechselbarkeit) : <span className="text-[0.75rem] text-[#5f646b]">{t('nicht geprüft', 'not checked')}</span>}</td>
                <td className="hidden sm:table-cell" />
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {(heute.handwerk || skizze.handwerk) && <BefundVergleich heute={heute.handwerk} skizze={skizze.handwerk} name={name} />}
      {/* Vorschläge zum Umbau (#199) gehören zum Check der heutigen Seite; ohne sie die der Skizze. */}
      {(() => {
        const liste = Array.isArray(heute.vorschlaege) && heute.vorschlaege.length > 0 ? heute.vorschlaege : skizze.vorschlaege;
        return Array.isArray(liste) && liste.length > 0 ? <Vorschlaege liste={liste} /> : null;
      })()}
    </article>
  );
}

const ANSICHT: Record<string, string> = { desktop: 'Desktop', handy: 'Handy', messung: 'Messung' };
const ANSICHT_EN: Record<string, string> = { desktop: 'Desktop', handy: 'Mobile', messung: 'Measurement' };

/** Fachprüfer heute und auf der Skizze, je Bereich und Ansicht eine Zeile. */
function BefundVergleich({ heute, skizze, name }: { heute: any; skizze: any; name: string }) {
  const { zuerst, weitere } = vergleicheBefunde(heute?.befunde ?? [], skizze?.befunde ?? []);
  const kleinere = (h: any) => Number(h?.kleinere) || 0;
  const staerken: string[] = (heute?.staerken?.length ? heute.staerken : skizze?.staerken ?? []).slice(0, 2);
  const mitGrad3 = zuerst.some((z) => Math.max(Number(z.heute?.schwere) || 0, Number(z.neu?.schwere) || 0) >= 3);
  const spalten = 'md:grid-cols-[10rem_minmax(0,1fr)_minmax(0,1fr)] print:grid-cols-[7.5rem_minmax(0,1fr)_minmax(0,1fr)]';
  const zelle = (b: any, seite: string, tun: boolean) => (
    <div className="flex min-w-0 flex-col gap-1">
      <span className="font-mono text-[0.625rem] font-bold tracking-[0.14em] text-[#5f646b] uppercase md:hidden print:hidden">{seite}</span>
      {b ? (
        <>
          <span className={`self-start rounded px-1.5 py-0.5 text-[0.6875rem] font-semibold ${Number(b.schwere) >= 3 ? STUFE['stark-minus'] : STUFE.minus}`}>{t('Grad', 'Grade')} {b.schwere}</span>
          <span className="text-[#1d1f26]">{b.was}</span>
          {tun && b.tun && <span className="text-[#565b62]">{t('Tun', 'To do')}: {b.tun}</span>}
        </>
      ) : <span className="text-[#5f646b]">{t('kein Befund', 'no finding')}</span>}
    </div>
  );
  // „Tun“ steht einmal je Zeile, bei der Skizze; fehlt sie, bei heute.
  const zeile = (z: BefundZeile, i: number) => (
    <li key={i} className={`grid gap-x-5 gap-y-3 border-t border-[#e8eceb] px-4 py-3.5 text-[0.875rem] leading-relaxed first:border-t-0 break-inside-avoid ${spalten}`}>
      <div>
        <div className="font-semibold text-[#1d1f26]">{bereichName(z.bereich)}</div>
        {z.ansicht && <div className="text-[0.75rem] text-[#5f646b]">{t(ANSICHT, ANSICHT_EN)[z.ansicht] ?? z.ansicht}</div>}
      </div>
      {zelle(z.heute, t('Heute', 'Today'), !z.neu)}
      {zelle(z.neu, t(`Mit ${name}`, `With ${name}`), true)}
    </li>
  );
  return (
    <div className="flex flex-col gap-3">
      <h3 className="m-0 font-mono text-[0.6875rem] font-bold tracking-[0.18em] text-[#5f646b] uppercase">
        {t('Was der Fachprüfer findet', 'What the expert reviewer finds')}{mitGrad3 ? t(', Grad 3 und 4', ', grade 3 and 4') : ''}
      </h3>
      <div className="overflow-hidden rounded-xl border border-[#dde3e1] bg-[#ffffff]">
        <div className={`hidden gap-x-5 border-b border-[#dde3e1] px-4 py-2 text-[0.75rem] text-[#5f646b] md:grid print:grid ${spalten}`}>
          <span>{t('Bereich', 'Area')}</span><span>{t('Heute', 'Today')}</span><span>{t(`Mit ${name} (Skizze)`, `With ${name} (sketch)`)}</span>
        </div>
        {zuerst.length > 0
          ? <ul className="m-0 list-none p-0">{zuerst.map(zeile)}</ul>
          : <p className="m-0 px-4 py-3 text-[0.875rem] text-[#5f646b]">{t('Kein Befund ab Grad 2.', 'No finding of grade 2 or higher.')}</p>}
        {weitere.length > 0 && (
          <details className="border-t border-[#e8eceb] print:hidden">
            <summary className="flex min-h-11 cursor-pointer items-center px-4 text-[0.8125rem] text-[#1f6964]">
              {t(`${weitere.length} weitere ${weitere.length === 1 ? 'Zeile' : 'Zeilen'} mit Grad 2 zeigen`, `Show ${weitere.length} more ${weitere.length === 1 ? 'row' : 'rows'} with grade 2`)}
            </summary>
            <ul className="m-0 list-none border-t border-[#e8eceb] p-0">{weitere.map(zeile)}</ul>
          </details>
        )}
      </div>
      <p className="m-0 max-w-[75ch] text-[0.8125rem] leading-relaxed text-[#5f646b]">
        {(kleinere(heute) > 0 || kleinere(skizze) > 0) && t(
          <>Dazu Feinschliff (Grad 0 bis 1): heute {kleinere(heute)}, mit {name} {kleinere(skizze)}. </>,
          <>Plus polish (grade 0 to 1): today {kleinere(heute)}, with {name} {kleinere(skizze)}. </>,
        )}
        {weitere.length > 0 && <span className="hidden print:inline">{t(`Weitere Zeilen mit Grad 2 im Bericht online: ${weitere.length}. `, `More rows with grade 2 in the online report: ${weitere.length}. `)}</span>}
        {t('Bei einer Skizze können Befunde zu Abständen, fehlenden Titeln oder losem Text unter Karten vom Einsetzen kommen, nicht von der Gestaltung.',
          'With a sketch, findings about spacing, missing titles or loose text under cards can come from inserting the text, not from the design.')}
      </p>
      {staerken.length > 0 && (
        <p className="m-0 max-w-[75ch] text-[0.875rem] leading-relaxed text-[#2f3238]">
          <span className="font-semibold text-[#1d1f26]">{t('Gut gelöst: ', 'Done well: ')}</span>{staerken.join(' ')}
        </p>
      )}
    </div>
  );
}

/**
 * Nur im PDF: die Texte im Wortlaut als Anhang (seit 08.10.2026). Vorher
 * klappte der Druck jede Fassung samt Vergleichsfassung daneben auf; beim
 * Magnolia-Bericht mit drei Fassungen waren das 94 Seiten, die
 * Vergleichsfassung dreimal und in schmalen Spalten. Jetzt steht jede Fassung
 * einmal in zwei Spalten, die Vergleichsfassung am Ende, ebenfalls einmal.
 * Liefen die Fassungen gegen verschiedene Vergleichstexte, steht jeder einmal.
 */
function AnhangTexte({ varianten }: { varianten: any[] }) {
  const mitText = varianten.map((x, i) => ({ x, nr: i + 1 })).filter(({ x }) => typeof x?.text === 'string' && x.text.trim());
  if (!mitText.length) return null;
  const vergleiche: { text: string; nr: number[] }[] = [];
  for (const { x, nr } of mitText) {
    const b = x.mode !== 'feedback' && typeof x.baseline_text === 'string' ? x.baseline_text.trim() : '';
    if (!b) continue;
    const da = vergleiche.find((v) => v.text === b);
    if (da) da.nr.push(nr); else vergleiche.push({ text: b, nr: [nr] });
  }
  const block = (titel: React.ReactNode, zeile: string, text: string, key: string) => (
    <section key={key} className="mt-6 first:mt-0">
      <div className="mb-3 flex items-baseline justify-between gap-4 border-t-2 border-[#1d1f26] pt-2 break-after-avoid">
        <h3 className={`${KOPF} m-0 text-[14pt]`}>{titel}</h3>
        <span className="text-right text-[8.5pt] text-[#5f646b]">{zeile}</span>
      </div>
      <div className="columns-2 gap-7 text-[9pt] leading-[1.45] text-[#1d1f26]">
        {abschnitte(text).map((a, i) => (
          <div key={i} className="mb-2 break-inside-avoid-column">
            {a.kopf && <p className="m-0 font-semibold break-after-avoid">{a.kopf}</p>}
            {a.rumpf && <p className="m-0 whitespace-pre-line">{a.rumpf}</p>}
          </div>
        ))}
      </div>
    </section>
  );
  return (
    <section className="hidden break-before-page print:block" aria-label={t('Anhang', 'Appendix')}>
      <Augenbraue>{t('Anhang · Die Texte im Wortlaut', 'Appendix · The texts in full')}</Augenbraue>
      <p className="mt-0 mb-5 max-w-[62ch] text-[9.5pt] text-[#2f3238]">
        {t('Jede Fassung steht einmal, so, wie die Simulation sie gelesen hat. Die Vergleichsfassung folgt am Ende. Online zeigt der Bericht beide nebeneinander.',
          'Each version appears once, as the simulation read it. The baseline follows at the end. Online, the report shows both side by side.')}
      </p>
      {mitText.map(({ x, nr }) => block(
        <><span className="mr-2 font-mono text-[9pt] text-[#5f646b]">F{nr}</span>{fassungsname(x.label)}</>,
        [x.mode === 'feedback'
          ? t(`${x.wins?.candidate ?? '—'} von ${x.wins?.total ?? '—'} würden weitergehen`, `${x.wins?.candidate ?? '—'} of ${x.wins?.total ?? '—'} would go on`)
          : `${zahl(x.scores?.candidate)} ${t('zu', 'to')} ${zahl(x.scores?.baseline)}`,
          x.verdict && x.mode !== 'feedback' ? urteilWort(x.verdict) ?? '' : '', zeichenZeile(x.text)].filter(Boolean).join(' · '),
        x.text, `f${nr}`,
      ))}
      {vergleiche.map((v, i) => block(
        vergleiche.length === 1 ? t('Vergleichsfassung', 'Baseline') : t(`Vergleichsfassung ${i + 1}`, `Baseline ${i + 1}`),
        `${v.nr.length === mitText.length ? t('für alle Fassungen', 'for all versions') : `${t('für', 'for')} ${v.nr.map((n) => `F${n}`).join(', ')}`} · ${zeichenZeile(v.text)}`,
        v.text, `v${i}`,
      ))}
    </section>
  );
}

/** So sind wir vorgegangen: die Schritte der Studie, je mit Warum. */
function Ablauf({ schritte, umfang }: { schritte: Schritt[]; umfang?: string }) {
  return (
    // Eine Zeitleiste statt Karten (seit 08.10.2026): die Karten waren ungleich
    // hoch, weil die dritte den ganzen Fachprüfer erklärte.
    <section className="mt-9 break-inside-avoid" aria-label={t('Die Schritte mit Datum', 'The steps with dates')}>
      {/* Keine Überschrift: der Ablauf steht im Akkordeon „Methode und Umfang“ (h3), und eine h2 hier hängte alle folgenden Akkordeons darunter. Die section trägt aria-label. */}
      <p className="mt-0 mb-4 font-mono text-[0.6875rem] font-bold tracking-[0.18em] text-[#5f646b] uppercase">{t('Die Schritte mit Datum', 'The steps with dates')}</p>
      {/* Der Umfang der ganzen Studie, auch wenn der Bericht nur einen Teil zeigt (seit 09.10.2026). */}
      {umfang && <p className="mt-0 mb-4 text-[0.9375rem] leading-relaxed text-[#1d1f26]">{umfang}</p>}
      <ol className={`m-0 grid list-none gap-x-6 gap-y-5 border-l-2 border-[#3f8a87] p-0 pl-4 sm:grid-cols-2 sm:border-l-0 sm:pl-0 print:grid-cols-4 print:border-l-0 print:pl-0 ${schritte.length >= 4 ? 'lg:grid-cols-4' : 'lg:grid-cols-3'}`}>
        {schritte.map((x, i) => (
          <li key={i} className="flex flex-col gap-1 sm:border-t-2 sm:border-[#3f8a87] sm:pt-3 print:border-t-2 print:border-[#3f8a87] print:pt-2">
            <span className="font-mono text-[0.75rem] font-bold text-[#1f6964]">
              {i + 1}{x.am ? <span className="ml-2 font-normal text-[#5f646b]">{tag(x.am)}</span> : null}
            </span>
            <span className={`${KOPF} text-[1.125rem] leading-snug`}>{x.titel}</span>
            <span className="text-[0.875rem] leading-relaxed text-[#1d1f26]">{x.was}</span>
            <span className="text-[0.8125rem] leading-relaxed text-[#5f646b]">{x.warum}</span>
          </li>
        ))}
      </ol>
    </section>
  );
}

function PersonaKarte({ p }: { p: any }) {
  const belege: any[] = Array.isArray(p.evidence) ? p.evidence : [];
  const zitat = belege.find((e: any) => e?.quote);
  const unter = [p.role, p.company_size].filter(Boolean).join(' · ');
  return (
    <li className="flex flex-col gap-1.5 rounded-md border border-[#cbd3d1] bg-[#f4f5f2] px-4 py-3.5 break-inside-avoid">
      <span className={`${KOPF} text-[1.0625rem] leading-snug`}>{p.name}{p.rolle === 'rand' && <RandMarke />}</span>
      {unter && <span className="text-[0.8125rem] leading-snug text-[#5f646b]">{unter}</span>}
      {zitat && (
        <p className="my-1 rounded bg-[#eef2f1] px-3 py-2 text-[0.875rem] leading-snug text-[#2f3238] italic font-serif">{t('„', '“')}{zitat.quote}{t('“', '”')}</p>
      )}
      <span className="mt-auto pt-1 font-mono text-[0.625rem] font-bold tracking-[0.16em] text-[#62676e] uppercase">
        {belege.length
          ? <>{belege.length} {belege.length === 1 ? t('Beleg', 'source') : t('Belege', 'sources')}: {belege.map((e: any, i: number) => <span key={i}>{i > 0 && ', '}<Beleg e={e} /></span>)}</>
          : t('ohne Beleg', 'no evidence')}
      </span>
    </li>
  );
}

/**
 * Eine Fassung als Zeile, aufgeklappt: Leseansicht (allein oder neben der
 * Vergleichsfassung, je Abschnitt markiert, was sich geändert hat), Text
 * kopieren, als Markdown laden, Link auf genau diese Fassung. Darunter Kern
 * und Rand, Noten je Persona (bei mehreren Fassungen) und die Einwände.
 */
function Fassung({ x, nr, mehrere }: { x: any; nr: number; mehrere: boolean }) {
  const vergleich = x.mode !== 'feedback' && !!x.baseline_text;
  const quelle = sichererLink(x.source_url);
  const stelle = `fassung-${nr}`;
  return (
    <details id={stelle} data-bericht className="group scroll-mt-6 border-b border-[#e8eceb] last:border-b-0">
      <summary className="flex cursor-pointer list-none flex-wrap items-center gap-x-5 gap-y-1.5 px-5 py-4 [&::-webkit-details-marker]:hidden">
        <span className="w-7 font-mono text-[0.75rem] text-[#5f646b]">F{nr}</span>
        <span className="min-w-0 flex-[1_1_16rem]">
          <span className="block font-semibold text-[#1d1f26] [overflow-wrap:anywhere]">{fassungsname(x.label)}</span>
          <span className="block text-[0.8125rem] text-[#5f646b]">
            {x.mode === 'feedback'
              ? t(<>{x.wins?.candidate ?? '—'} von {x.wins?.total ?? '—'} Personas würden weitergehen</>, <>{x.wins?.candidate ?? '—'} of {x.wins?.total ?? '—'} personas would go on</>)
              : t(
                <>{zahl(x.scores?.candidate)} zu {zahl(x.scores?.baseline)} · {x.wins?.candidate ?? '—'} von {x.wins?.total ?? '—'}{urteilEinheit(x)}{leseKernRand(x) ? ' im Kern' : ''} dafür</>,
                <>{zahl(x.scores?.candidate)} to {zahl(x.scores?.baseline)} · {x.wins?.candidate ?? '—'} of {x.wins?.total ?? '—'}{urteilEinheit(x)}{leseKernRand(x) ? ' in the core' : ''} in favor</>,
              )}
            {' · '}{zeichenZeile(x.text)}{quelle ? ' · live' : ''}
          </span>
        </span>
        <UrteilEtikett v={x} />
        {/* Am Handy ist die ganze Zeile der Knopf; ein eigener Kasten je Fassung
            stand dort unter dem Etikett in einer eigenen Zeile. */}
        <span className="hidden rounded-lg bg-[#1f6964] px-3.5 py-2 text-[0.8125rem] font-semibold text-white group-open:hidden sm:inline">{t('Text lesen', 'Read text')}</span>
        <span className="text-[0.8125rem] font-semibold text-[#1f6964] group-open:hidden sm:hidden">{t('Lesen ›', 'Read ›')}</span>
        <span className="hidden rounded-lg border border-[#b7c0be] px-3 py-1.5 text-[0.8125rem] font-semibold text-[#565b62] group-open:inline">{t('Zuklappen', 'Collapse')}</span>
      </summary>
      <div className="flex flex-col gap-6 px-5 pb-6">
        <p className="m-0 text-[0.8125rem] text-[#5f646b]">
          {tag(x.created_at)}
          {quelle && <> · <a className="text-[#1f6964] underline underline-offset-2" href={quelle} rel="noopener noreferrer nofollow">{t('Live-Seite', 'Live page')}</a></>}
          {vergleich && <> · {t('Vergleichsfassung', 'Baseline')} {zeichenZeile(x.baseline_text)}</>}
        </p>
        <Leseansicht x={x} stelle={stelle} vergleich={vergleich} />
        <KernUndMarke v={x} klein vergleich={vergleichFuerSatz(null, x)} />
        <BefundeJeAbschnitt x={x} />
        {mehrere && <NotenJePersonaImBericht x={x} />}
        {x.blockers?.length ? (
          <div>
            <h4 className="mt-0 mb-1 font-mono text-[0.6875rem] font-bold tracking-[0.18em] text-[#5f646b] uppercase break-after-avoid">{t('Wo es hakt', 'Where it sticks')}</h4>
            <ul className="m-0 list-none p-0">
              {x.blockers.map((b: any, i: number) => <Einwand key={i} b={b} />)}
            </ul>
          </div>
        ) : (
          <p className="m-0 text-sm text-[#5f646b]">{t('Keine Einwände erfasst.', 'No objections recorded.')}</p>
        )}
      </div>
    </details>
  );
}

const AENDERUNG: Record<Aenderung, { wort: string; farbe: string }> = {
  umformuliert: { wort: 'umformuliert', farbe: STUFE.plus },
  neu: { wort: 'neu', farbe: STUFE.plus },
  gestrichen: { wort: 'gestrichen', farbe: STUFE.minus },
  gleich: { wort: 'gleich', farbe: STUFE.knapp },
};
const AENDERUNG_EN: Record<Aenderung, string> = { umformuliert: 'reworded', neu: 'new', gestrichen: 'removed', gleich: 'same' };
const aenderungWort = (a: Aenderung) => t(AENDERUNG[a].wort, AENDERUNG_EN[a]);

/**
 * Der Text so, wie die Simulation ihn gelesen hat, in Abschnitten. Neben der
 * Vergleichsfassung je Abschnitt markiert, ob er gleich, umformuliert, neu
 * oder gestrichen ist; unveränderte Abschnitte stehen nur einmal.
 */
function Leseansicht({ x, stelle, vergleich }: { x: any; stelle: string; vergleich: boolean }) {
  const [daneben, setDaneben] = useState(vergleich);
  const [meldung, setMeldung] = useState('');
  const text = String(x.text ?? '');
  // Mit Gliederung oder ohne Überschriften (ein Hero) je Abschnitt mit Rolle
  // und markierten Wörtern (fassungen-abgleich.ts, seit 27.10.2026); sonst nach
  // Überschriften wie vorher.
  const zeilen: BerichtZeile[] = berichtsZeilen(x, daneben && vergleich) ?? (daneben && vergleich
    ? vergleicheAbschnitte(text, x.baseline_text)
    : abschnitte(text).map((a) => ({ kopf: a.kopf, aenderung: null as Aenderung | null, neu: a, alt: null })));
  const kurz = (s: string) => { setMeldung(s); window.setTimeout(() => setMeldung(''), 2500); };
  const kopieren = async (was: string, ok: string) => {
    try { await navigator.clipboard.writeText(was); kurz(ok); } catch { kurz(t('Kopieren ging nicht. Bitte von Hand markieren.', 'Copying did not work. Please select the text by hand.')); }
  };
  const laden = () => {
    const blob = new Blob([text], { type: 'text/markdown;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const ersatz = t('fassung', 'version');
    a.download = `${(fassungsname(x.label) || ersatz).replace(/[^\p{L}\p{N}]+/gu, '-').replace(/^-|-$/g, '').toLowerCase() || ersatz}.md`;
    a.click();
    URL.revokeObjectURL(url);
  };
  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center justify-between gap-3 border-y border-[#dde3e1] py-2.5 print:hidden">
        {vergleich ? (
          <div role="group" aria-label={t('Ansicht', 'View')} className="inline-flex rounded-lg bg-[#eef2f1] p-[3px] text-[0.8125rem]">
            {([[t('Nur diese Fassung', 'Only this version'), false], [t('Neben der Vergleichsfassung', 'Next to the baseline'), true]] as const).map(([wort, wert]) => (
              <button key={wort} type="button" aria-pressed={daneben === wert} onClick={() => setDaneben(wert)}
                className={`min-h-11 cursor-pointer rounded-md px-3 ${daneben === wert ? 'bg-[#ffffff] font-semibold text-[#1d1f26] shadow-[0_1px_2px_rgb(20_18_12/0.08)]' : 'text-[#565b62]'}`}>
                {wort}
              </button>
            ))}
          </div>
        ) : <span />}
        <div className="flex flex-wrap items-center gap-2 text-[0.8125rem]">
          <span role="status" className="text-[#1f6964]">{meldung}</span>
          <button type="button" onClick={() => kopieren(text, t('Text kopiert.', 'Text copied.'))} className="min-h-11 cursor-pointer rounded-full border border-[#b7c0be] bg-[#ffffff] px-3 text-[#1d1f26] hover:border-[#1f6964]">{t('Text kopieren', 'Copy text')}</button>
          <button type="button" onClick={laden} className="min-h-11 cursor-pointer rounded-full border border-[#b7c0be] bg-[#ffffff] px-3 text-[#1d1f26] hover:border-[#1f6964]">{t('Als Markdown laden', 'Download as Markdown')}</button>
          <button type="button" onClick={() => kopieren(`${window.location.origin}${window.location.pathname}${window.location.search}#${stelle}`, t('Link kopiert.', 'Link copied.'))}
            className="min-h-11 cursor-pointer rounded-full bg-[#1f6964] px-3 font-semibold text-white hover:bg-[#174f4b]">{t('Link zu dieser Fassung', 'Link to this version')}</button>
        </div>
      </div>
      {daneben && vergleich && (
        <p className="m-0 flex flex-wrap gap-x-4 gap-y-1 text-[0.75rem] text-[#565b62]">
          {(['umformuliert', 'neu', 'gestrichen', 'gleich'] as const).map((a) => (
            <span key={a} className="inline-flex items-center gap-1.5"><span className={`rounded px-1.5 font-semibold ${AENDERUNG[a].farbe}`}>{aenderungWort(a)}</span></span>
          ))}
          <span>{t('Die Simulation hat genau diesen Text gelesen. Die Gestaltung der Seite sieht anders aus.', 'The simulation read exactly this text. The design of the page looks different.')}</span>
        </p>
      )}
      <div className="overflow-hidden rounded-lg border border-[#dde3e1]">
        {zeilen.length === 0 && <p className="m-0 px-4 py-3 text-[#5f646b]">—</p>}
        {zeilen.map((z, i) => (
          <div key={i} className="grid gap-x-5 gap-y-2 border-t border-[#e8eceb] px-4 py-3.5 first:border-t-0 break-inside-avoid sm:grid-cols-[8rem_minmax(0,1fr)]">
            <div className="flex flex-row flex-wrap items-center gap-2 sm:flex-col sm:items-start">
              {z.rolle && <span className="font-mono text-[0.625rem] font-bold tracking-[0.14em] text-[#5f646b] uppercase">{z.rolle}</span>}
              {z.aenderung && <span className={`rounded px-1.5 text-[0.6875rem] font-semibold ${AENDERUNG[z.aenderung].farbe}`}>{aenderungWort(z.aenderung)}</span>}
            </div>
            <div className={`grid min-w-0 gap-3 ${z.aenderung === 'umformuliert' ? 'md:grid-cols-2' : ''}`}>
              {z.neu && <Textstueck a={z.neu} teile={z.woerter?.neu} />}
              {z.alt && z.aenderung !== 'gleich' && (
                <div className="rounded-md bg-[#f9fbfa] px-3 py-2">
                  <span className="mb-1 block font-mono text-[0.625rem] font-bold tracking-[0.14em] text-[#1f6964] uppercase">{t('Vergleichsfassung', 'Baseline')}</span>
                  <Textstueck a={z.alt} leise teile={z.woerter?.alt} />
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/** Ein Abschnitt; mit `teile` die Wörter markiert, die in der anderen Fassung anders stehen. */
function Textstueck({ a, leise = false, teile }: { a: { kopf: string; rumpf: string }; leise?: boolean; teile?: Teil[] }) {
  return (
    <div className="min-w-0 [overflow-wrap:anywhere]">
      {a.kopf && <div className={`mb-1 font-semibold leading-snug ${leise ? 'text-[#565b62]' : 'text-[#1d1f26]'}`}>{a.kopf}</div>}
      {a.rumpf && (
        <p className={`m-0 whitespace-pre-line leading-relaxed ${leise ? 'text-[0.875rem] text-[#565b62]' : 'text-[0.9375rem] text-[#2f3238]'}`}>
          {teile
            ? teile.map((x, i) => !x.anders ? <span key={i}>{x.text}</span>
              : leise ? <del key={i} className="decoration-[#a4332a]/60">{x.text}</del>
                : <mark key={i} className="rounded-sm bg-[#3f8a87]/20 px-0.5 text-[#1d1f26]">{x.text}</mark>)
            : a.rumpf}
        </p>
      )}
    </div>
  );
}

/** Ein Einwand mit der Note dieser Persona als Balken. Die Note gilt der geprüften Fassung. */
function Einwand({ b }: { b: any }) {
  const n = typeof b.score === 'number' ? b.score : null;
  return (
    <li className="grid gap-x-5 gap-y-1.5 border-t border-[#1d1f26]/10 py-4 first:border-t-0 break-inside-avoid sm:grid-cols-[11rem_1fr]">
      <div>
        <div className="text-[0.875rem] font-semibold text-[#1d1f26]">{b.persona}{b.rolle === 'rand' && <RandMarke />}</div>
        {n != null && (
          <div className="mt-1.5 flex items-center gap-2">
            <span className="h-1.5 w-20 overflow-hidden rounded-full bg-[#e3e8e6]" aria-hidden>
              <span className="block h-full rounded-full bg-[#3f8a87]" style={{ width: `${Math.min(100, Math.max(0, n * 10))}%` }} />
            </span>
            <span className="text-[0.75rem] text-[#5f646b] tabular-nums">{n}/10</span>
          </div>
        )}
      </div>
      <div className="min-w-0">
        <p className="m-0 leading-relaxed">{b.einwand}</p>
        {b.unklar && <p className="mt-1 mb-0 text-sm text-[#5f646b]">{t('Unklar', 'Unclear')}: {b.unklar}</p>}
        {b.absprung && <p className="mt-1 mb-0 text-sm text-[#5f646b]">{t('Absprung', 'Drop-off')}: {b.absprung}</p>}
        {b.quelle && <Quelle q={b.quelle} />}
      </div>
    </li>
  );
}

/** Quellen kommen aus Kundendaten. Der Link geht durch `sichererLink` oder gar nicht. */
function Quelle({ q }: { q: any }) {
  const url = sichererLink(q.url);
  return (
    <p className="mt-1.5 mb-0 text-sm text-[#5f646b]">
      {url
        ? <a className="text-[#1f6964] underline underline-offset-[3px]" href={url} rel="noopener noreferrer nofollow">{q.typ ?? t('Quelle', 'Source')}</a>
        : <span>{q.typ ?? t('Quelle', 'Source')}</span>}
      {q.quote && <span className="ml-2 italic">{t('„', '“')}{q.quote}{t('“', '”')}</span>}
    </p>
  );
}

function Beleg({ e }: { e: any }) {
  const url = sichererLink(e.url);
  return url
    ? <a className="text-[#1f6964] underline underline-offset-2" href={url} rel="noopener noreferrer nofollow">{e.typ || t('Quelle', 'Source')}</a>
    : <span>{e.typ || t('Quelle', 'Source')}</span>;
}

/**
 * Noten je Persona einer einzelnen Fassung, im aufgeklappten Teil. Dieselbe
 * Komponente wie auf der Lauf-Detailseite (`noten-je-persona.tsx`). Eingefrorene
 * Berichte von vor dem 26.09.2026 haben das Feld nicht; dann fehlt der Abschnitt.
 */
function NotenJePersonaImBericht({ x }: { x: any }) {
  if (x.mode === 'feedback' || personaNoten(x).length === 0) return null;
  return (
    <div>
      <h4 className="mt-0 mb-1 font-mono text-[0.6875rem] font-bold tracking-[0.18em] text-[#5f646b] uppercase break-after-avoid">{t('Noten je Persona', 'Scores by persona')}</h4>
      <NotenJePersona quelle={x} />
    </div>
  );
}

/**
 * Nächster Schritt und Befunde je Abschnitt (seit 09.10.2026, befunde.ts).
 * Je Fassung im aufgeklappten Text, auch für die Vergleichsfassung und
 * frühere Fassungen; die letzte Fassung steht oben als „Befunde je
 * Abschnitt“ (AbschnittsListe). Berichte ohne die Felder zeigen hier nichts.
 */
function BefundeJeAbschnitt({ x }: { x: any }) {
  const teile = befundeDerFassung(x);
  if (!teile.length) return null;
  return (
    <div className="flex flex-col gap-4">
      {teile.map((teil) => (
        <div key={teil.titel} className="break-inside-avoid">
          <h4 className="mt-0 mb-1 font-mono text-[0.6875rem] font-bold tracking-[0.18em] text-[#5f646b] uppercase break-after-avoid">{teil.titel}: {t('nächster Schritt und Abschnitte', 'next step and sections')}</h4>
          {teil.schritt && <p className="m-0 mb-2 text-[0.875rem] text-[#2f3238]">{t('Würden den nächsten Schritt gehen:', 'Would take the next step:')} {schrittSatz(teil.schritt)}.</p>}
          {teil.abschnitte.length > 0 && (
            // Bei 340 px breiter als der Behälter: ein fokussierbarer, benannter Bereich, damit er per Tastatur scrollbar ist.
            <div className="overflow-x-auto" tabIndex={0} role="region" aria-label={t(`${teil.titel}: Abschnitte, seitlich scrollbar`, `${teil.titel}: sections, scrolls sideways`)}>
              <table className="w-full border-collapse text-left text-[0.8125rem] text-[#2f3238]">
                <thead>
                  <tr className="border-b border-[#cbd3d1] text-[#5f646b]">
                    <th className="py-1 pr-3 font-semibold">{t('Abschnitt', 'Section')}</th>
                    <th className="py-1 pr-3 font-semibold">{artWort('wirkt')}</th>
                    <th className="py-1 pr-3 font-semibold">{artWort('bremst')}</th>
                    <th className="py-1 pr-3 font-semibold">{artWort('fehlt')}</th>
                    <th className="py-1 font-semibold">{t('Typische Aussage', 'Typical statement')}</th>
                  </tr>
                </thead>
                <tbody>
                  {teil.abschnitte.map((a) => {
                    const stimme = a.bremst.stimmen[0] ?? a.fehlt.stimmen[0] ?? a.wirkt.stimmen[0];
                    return (
                      <tr key={a.abschnitt} className="border-b border-[#e8eceb] align-top">
                        <td className="py-1.5 pr-3 [overflow-wrap:anywhere]">{a.abschnitt} <span className="text-[#5f646b]">({a.personas} {t('von', 'of')} {a.von})</span></td>
                        <td className="py-1.5 pr-3 whitespace-nowrap">{befundZahl(a.wirkt)}</td>
                        <td className="py-1.5 pr-3 whitespace-nowrap">{befundZahl(a.bremst)}</td>
                        <td className="py-1.5 pr-3 whitespace-nowrap">{befundZahl(a.fehlt)}</td>
                        <td className="py-1.5 [overflow-wrap:anywhere]">{stimme ? <>{sprache() === 'en' ? zitiert(stimme.text) : <>„{stimme.text}“</>} <span className="text-[#5f646b]">{stimme.persona}</span></> : '–'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

/** Etikett an Personas der erweiterten Zielgruppe (Randpersonas), im Papier-Look des Berichts. */
function RandMarke() {
  return (
    <span className="ml-2 inline-block rounded border border-[#cbd3d1] bg-[#eef2f1] px-1.5 py-px align-[0.12em] font-mono text-[0.625rem] font-bold tracking-[0.12em] whitespace-nowrap text-[#5f646b] uppercase [font-family:ui-monospace,monospace]">
      {t('erweiterte Zielgruppe', 'extended audience')}
    </span>
  );
}

/**
 * Kern und Rand, „gefällt allen ein bisschen" und „hält das Versprechen" je
 * Fassung (seit 27.09.2026). Eingefrorene Berichte davor tragen keines der
 * Felder; dann rendert hier nichts, und der Bericht sieht aus wie vorher.
 * Die Warnung „gewinnt nur am Rand" steht seit 07.10.2026 sandfarben statt rot:
 * sie ordnet ein, sie meldet keinen Fehler. Seit 12.10.2026 baut die Seite den
 * Satz selbst aus den Zahlen (`randWarnung`), der eingefrorene Wortlaut des
 * Servers ist nur noch der Rückfall.
 */
function KernUndMarke({ v, klein = false, nurHinweise = false, ohneWarnung = false, vergleich }: {
  v: any; klein?: boolean; nurHinweise?: boolean; ohneWarnung?: boolean; vergleich?: string;
}) {
  // nurHinweise: oben im Bericht (Schritt 3, unter den Karten) nur Muster und
  // Versprechen; die Zahlen zu Kern und erweiterter Zielgruppe stehen in
  // Schritt 2 und im Aufklapper. ohneWarnung: die Warnung steht seit 12.10.2026
  // in der Karte der Fassung, nicht mehr als eigener Block.
  const kr = v.mode === 'feedback' ? null : leseKernRand(v);
  const krw = v.mode === 'feedback' || nurHinweise ? null : leseKernRandWeiter(v);
  const muster = v.mode === 'feedback' ? null : leseMuster(v);
  const bilanz = leseBilanz(v.versprechen);
  const warnung = kr && !ohneWarnung ? (randWarnung(kr, { vergleich, urteile: urteilEinheit(v) !== '' }) ?? kr.warnung) : null;
  if (nurHinweise && !warnung && !muster && !bilanz) return null;
  if (!kr && !krw && !muster && !bilanz) return null;
  const text = klein ? 'text-[0.8125rem]' : 'text-[0.875rem]';
  return (
    <div className={`${klein ? '' : nurHinweise ? 'mt-1' : 'mt-5'} flex max-w-[62ch] flex-col gap-2 break-inside-avoid`}>
      {kr && !nurHinweise && <p className={`m-0 ${text} leading-relaxed text-[#5f646b]`}>{randZeile(kr)}</p>}
      {krw && <p className={`m-0 ${text} leading-relaxed text-[#5f646b]`}>{randZeileWeiter(krw)}</p>}
      {warnung && (
        <p className={`m-0 rounded-md border border-[#cbd3d1] bg-[#eef2f1] px-3.5 py-2.5 ${text} leading-relaxed text-[#2f3238]`}>{warnung}</p>
      )}
      {muster && (
        <p className={`m-0 rounded-md border border-[#cbd3d1] bg-[#eef2f1] px-3.5 py-2.5 ${text} leading-relaxed text-[#2f3238]`}>{musterSatz(muster)}</p>
      )}
      {bilanz && (
        <p className={`m-0 flex flex-wrap items-center gap-x-2.5 gap-y-1 ${text} text-[#5f646b]`}>
          <span className="h-1.5 w-20 overflow-hidden rounded-full bg-[#e3e8e6]" aria-hidden>
            <span className="block h-full rounded-full bg-[#3f8a87]" style={{ width: `${(bilanz.haelt / bilanz.von) * 100}%` }} />
          </span>
          <span>{t('Markenversprechen', 'Brand promise')}: {bilanzSatz(bilanz)} {t('Zählt nicht in den Score.', 'Does not count toward the score.')}</span>
        </p>
      )}
    </div>
  );
}

/** Kleine Dachzeile wie im Entwurf: Versalien, gesperrt. */
function Kick({ children }: { children: React.ReactNode }) {
  return <span className="text-[0.6875rem] font-semibold tracking-[0.12em] text-[#5f646b] uppercase sm:text-[0.71875rem]">{children}</span>;
}

/**
 * „Die geprüfte Seite“ (seit 09.10.2026, gepruefteSeite in bericht-blatt.ts),
 * seit 12.10.2026 in Schritt 1 der Zeitleiste: der erste Bildschirm am Desktop und am Handy
 * nebeneinander, abgeschnitten per object-fit. Ein Klick öffnet die ganze
 * Aufnahme in einem neuen Tab. Bei 340 px untereinander, das Handy mittig.
 */
function GepruefteSeiteBlock({ s }: { s: GepruefteSeite }) {
  const bild = (art: 'desktop' | 'mobil', src: string) => (
    <figure key={art} className={`m-0 flex min-w-0 flex-col gap-1.5 ${art === 'desktop' ? 'w-full sm:flex-1' : 'w-full max-w-[220px] sm:w-[176px] sm:flex-none print:w-[40mm]'}`}>
      <a href={src} target="_blank" rel="noopener noreferrer"
        className="block overflow-hidden rounded-[10px] border border-[#dde3e1] bg-white focus-visible:outline-3 focus-visible:outline-offset-2 focus-visible:outline-[#1f6964]">
        <img src={src} alt={t(`Erster Bildschirm der geprüften Seite ${art === 'desktop' ? 'am Desktop' : 'am Handy'}`, `First screen of the tested page ${art === 'desktop' ? 'on desktop' : 'on mobile'}`)}
          className={`block w-full object-cover object-top ${art === 'desktop'
            ? 'h-[190px] sm:h-[300px] lg:h-[360px] print:h-[62mm]'
            : 'h-[400px] sm:h-[300px] lg:h-[360px] print:h-[62mm]'}`} />
        <span className="sr-only">{t(' (ganze Aufnahme in neuem Tab öffnen)', ' (open full capture in a new tab)')}</span>
      </a>
      <figcaption className="text-[0.6875rem] font-semibold tracking-[0.12em] text-[#5f646b] uppercase">{art === 'desktop' ? 'Desktop' : t('Handy', 'Mobile')}</figcaption>
    </figure>
  );
  return (
    <figure aria-labelledby="gepruefte-seite" className="m-0 mt-1 flex flex-col gap-3 break-inside-avoid">
      <p id="gepruefte-seite" className="m-0 text-[0.6875rem] font-semibold tracking-[0.12em] text-[#5f646b] uppercase sm:text-[0.71875rem]">{t('Die geprüfte Seite', 'The tested page')}</p>
      <div className="flex flex-col items-center gap-4 sm:flex-row sm:items-start sm:gap-5 print:flex-row print:items-start">
        {s.desktop && bild('desktop', s.desktop)}
        {s.mobil && bild('mobil', s.mobil)}
      </div>
      <p className="m-0 flex flex-wrap items-baseline gap-x-4 gap-y-1 text-[0.875rem] leading-relaxed text-[#565b62]">
        <span>
          {s.herkunft.art === 'erster_bildschirm' ? t(`${s.herkunft.variante} aus dem Vergleich der ersten Bildschirme. `, `${s.herkunft.variante} from the comparison of first screens. `) : ''}
          {t('Above the Fold, so wie die Personas die Seite vor dem Scrollen gesehen haben.', 'Above the fold, as the personas saw the page before scrolling.')}
        </span>
        {s.url && (
          <a href={s.url} target="_blank" rel="noopener noreferrer nofollow" className="font-semibold whitespace-nowrap text-[#1f6964] underline-offset-[3px] hover:underline">
            {t('Seite ansehen', 'View page')}<span className="sr-only">{t(' (neuer Tab)', ' (new tab)')}</span>
          </a>
        )}
      </p>
    </figure>
  );
}

/** Ein Kasten im Kopf: Getestet, Geurteilt haben, Ergebnis (der letzte leicht petrol hinterlegt; bis 13.10.2026 „Herausgekommen“). */
function Kasten({ titel, betont = false, children }: { titel: string; betont?: boolean; children: React.ReactNode }) {
  return (
    <div className={`flex min-w-0 flex-col gap-1.5 border-b border-[#e8eceb] px-[18px] py-4 last:border-b-0 sm:border-r sm:border-b-0 sm:last:border-r-0 print:border-r print:border-b-0 print:last:border-r-0 ${betont ? 'bg-[#eef4f3]' : ''}`}>
      <dt className={`text-[0.75rem] font-semibold tracking-[0.07em] uppercase ${betont ? 'text-[#1f6964]' : 'text-[#5f646b]'}`}>{titel}</dt>
      <dd className="m-0 text-[0.9375rem] leading-normal text-[#2a2c33]">{children}</dd>
    </div>
  );
}

/**
 * Ein Schritt der Zeitleiste: Kreis mit Nummer, senkrechte Linie bis zum
 * nächsten Schritt. `offen` (nur „Nächste Runde“): noch nicht getestet, Kreis
 * weiß mit gestricheltem Rand und das Etikett neben dem Titel.
 */
function Zeitschritt({ nr, titel, letzter, offen = false, children }: { nr: number; titel: string; letzter: boolean; offen?: boolean; children: React.ReactNode }) {
  const kopf = <h3 className="m-0 text-[1.0625rem] leading-snug font-semibold text-[#1d1f26]"><span className="sr-only">{t('Schritt', 'Step')} {nr}: </span>{titel}</h3>;
  return (
    <li className="grid grid-cols-[28px_minmax(0,1fr)] gap-x-3.5 sm:grid-cols-[32px_minmax(0,1fr)] sm:gap-x-4">
      <div className="flex flex-col items-center" aria-hidden>
        <span className={offen
          ? 'flex size-7 flex-none items-center justify-center rounded-full border-2 border-dashed border-[#3f8a87] bg-white text-[0.875rem] font-semibold text-[#1f6964]'
          : 'flex size-7 flex-none items-center justify-center rounded-full bg-[#1f6964] text-[0.875rem] font-semibold text-white'}>{nr}</span>
        {!letzter && <span className="w-0.5 flex-1 bg-[#dde3e1]" />}
      </div>
      <div className={`flex min-w-0 flex-col ${offen ? 'gap-3.5' : 'gap-2.5'} pt-0.5 ${letzter ? 'pb-2' : 'pb-7'}`}>
        {offen ? (
          <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
            {kopf}
            <span className="rounded-md border border-[#b7c0be] px-2 py-0.5 text-[0.75rem] font-semibold text-[#565b62]">{t('noch nicht getestet', 'not tested yet')}</span>
          </div>
        ) : kopf}
        {children}
      </div>
    </li>
  );
}

/**
 * Inhalt der Nächsten Runde: die Einleitung, dann je offene Frage eine Karte
 * mit Frage, Test und „Braucht von euch“. Bewusst ohne Knopf, Kontakt oder
 * Preis: der Bericht erklärt, er wirbt nicht. Im Druck sichtbar, bei 340 px
 * untereinander (die Spalte ist dort schmaler als 240 px).
 */
function NaechsteRundeBlock({ r }: { r: NaechsteRunde }) {
  return (
    <>
      <p className="m-0 max-w-[64ch] text-[0.9375rem] leading-relaxed text-[#565b62]">{r.einleitung}</p>
      <ul className="m-0 grid list-none grid-cols-[repeat(auto-fit,minmax(min(240px,100%),1fr))] gap-3 p-0">
        {r.fragen.map((f, i) => (
          <li key={i} className="flex min-w-0 flex-col gap-2 rounded-xl border border-dashed border-[#b7c0be] bg-[#f9fbfa] p-4 break-inside-avoid">
            <span className="text-base leading-[1.35] font-semibold text-[#1d1f26] [overflow-wrap:anywhere]">{f.frage}</span>
            {f.wie && <span className="text-[0.875rem] leading-normal text-[#565b62]">{f.wie}</span>}
            {f.braucht && (
              <span className="border-t border-[#e8eceb] pt-2 text-[0.8125rem] leading-normal text-[#2a2c33]">
                <b className="font-semibold">{t('Braucht von euch:', 'Needs from you:')}</b> {f.braucht}
              </span>
            )}
          </li>
        ))}
      </ul>
    </>
  );
}

/**
 * Die Skala der Wortstufen: fünf Segmente von klar schwächer (orange) bis klar
 * besser (teal), das zutreffende gefärbt. Höchstens 140 px breit, schmaler,
 * wenn die Karte es ist.
 */
const STUFEN_FARBE = ['#d07a3e', '#e8b48c', '#8b9492', '#8fc0bc', '#3f8a87'];
function Skala({ stufe }: { stufe: number }) {
  return (
    <span className="flex w-[140px] max-w-full min-w-0 shrink gap-1" aria-hidden>
      {STUFEN_FARBE.map((f, i) => <span key={i} className="h-2 min-w-0 flex-1 rounded-full" style={{ background: i === stufe ? f : '#e8eceb' }} />)}
    </span>
  );
}

/**
 * Schritt „Erster Bildschirm“: das Urteil, je Variante ein kleines Bild vom
 * Handy, der Anteil als Balken und wie viele zuerst dort klicken. Die zuerst
 * geklickte steht vorn. Bilder und alle Zahlen stehen im Akkordeon.
 */
function ErsterBildschirmBlock({ s }: { s: ErsterBildschirmSchritt }) {
  return (
    <>
      {s.urteil && <p className="m-0 max-w-[64ch] text-[0.9375rem] leading-relaxed text-[#1d1f26]">{s.urteil}</p>}
      <p className="m-0 max-w-[64ch] text-[0.9375rem] leading-relaxed text-[#565b62]">
        {t('Hier entscheidet sich, ob jemand bleibt. Jede Persona sah von allen Varianten, was vor dem Scrollen zu sehen ist, nebeneinander, ohne Namen und in wechselnder Reihenfolge, und sagte, wo sie zuerst klicken würde.',
          'This is where it is decided whether someone stays. For all variants, each persona saw what is visible before scrolling, side by side, without names and in changing order, and said where it would click first.')}
        {s.herkunft ? t(` Herkunft der Besucher: ${s.herkunft}`, ` Where visitors come from: ${s.herkunft}`) : ''}
      </p>
      <ul className="m-0 flex list-none flex-col border-t border-[#e8eceb] p-0">
        {s.varianten.map((v) => (
          <li key={v.nummer} className={`flex items-center gap-3.5 border-b border-[#e8eceb] px-3 py-3 break-inside-avoid ${v.vorn ? 'bg-[#eef4f3]' : ''}`}>
            {v.bild ? (
              <img src={v.bild} alt={t(`Erster Bildschirm von ${v.name}`, `First screen of ${v.name}`)} className="block h-[72px] w-[44px] flex-none rounded-[5px] border border-[#dde3e1] bg-[#eef2f1] object-cover object-top" />
            ) : (
              <span aria-hidden className="block h-[72px] w-[44px] flex-none rounded-[5px] border border-dashed border-[#cbd3d1]" />
            )}
            <span className="flex min-w-0 flex-1 flex-col gap-1.5">
              {v.vorn && <span className="text-[0.75rem] font-semibold tracking-[0.04em] text-[#1f6964] uppercase">{t('Zuerst geklickt', 'Clicked first')}</span>}
              <span className="text-[0.96875rem] leading-snug font-semibold text-[#1d1f26] [overflow-wrap:anywhere]">{v.name}</span>
              <span className="flex flex-wrap items-center gap-x-3 gap-y-1">
                <span className="block h-2 w-[140px] max-w-full overflow-hidden rounded-full bg-[#e8eceb]" aria-hidden>
                  <span className="block h-full rounded-full bg-[#3f8a87]" style={{ width: `${Math.round(v.anteil * 100)}%` }} />
                </span>
                <span className="text-[0.84375rem] leading-normal text-[#565b62] tabular-nums">
                  {t(`${v.zuerst} von ${s.personas} klicken hier zuerst`, `${v.zuerst} of ${s.personas} click here first`)}{v.kern ? ` · ${v.kern}` : ''}
                </span>
                {v.bleiben && <span className="text-[0.84375rem] leading-normal text-[#565b62] tabular-nums">{v.bleiben}</span>}
              </span>
            </span>
          </li>
        ))}
      </ul>
      <a href={`#${s.anker}`} className="self-start text-[0.875rem] font-semibold text-[#1f6964] underline-offset-[3px] hover:underline print:hidden">{t('Bilder und alle Zahlen', 'Images and all numbers')}</a>
    </>
  );
}

/**
 * Eine Fassung in Schritt 3: Etikett, Name, Ansatz und Länge, Link zum
 * Wortlaut. Seit 13.10.2026 ohne Urteil und ohne Rahmen für die beste: das
 * Ergebnis steht im eigenen Schritt danach (`ErgebnisListe`).
 */
function FassungsKarteAnsicht({ k, link }: { k: FassungsKarte; link: string }) {
  return (
    <div className="flex min-w-0 flex-col gap-2 rounded-xl border border-[#dde3e1] p-4 break-inside-avoid">
      {k.von && (
        <span className={`self-start rounded-md px-2 py-0.5 text-[0.75rem] font-semibold ${k.von === 'uns' ? 'bg-[#1f6964] text-white' : 'border border-[#b7c0be] text-[#565b62]'}`}>
          {VON_ETIKETT[k.von]}
        </span>
      )}
      <span className="text-base leading-snug font-semibold text-[#1d1f26] [overflow-wrap:anywhere]">{k.name}</span>
      {(k.ansatz || k.laenge) && (
        <span className="text-[0.875rem] leading-normal text-[#565b62]">{[k.ansatz, k.laenge ? `${gross(k.laenge)}.` : ''].filter(Boolean).join(' ')}</span>
      )}
      <a href={`#${k.anker}`} className="mt-auto self-start pt-1 text-[0.875rem] font-semibold text-[#1f6964] underline-offset-[3px] hover:underline print:hidden">{link}</a>
    </div>
  );
}

/**
 * Oben in Schritt 3: was nacheinander geprüft wird, Text- und Design-Simulation
 * als zwei Kästen (bei 340 px untereinander). Ohne Gestaltungs-Schritt nur der
 * Kasten zum Text.
 */
function PruefungenBlock({ p }: { p: Pruefung[] }) {
  return (
    <ol className={`m-0 grid list-none gap-3 p-0 ${p.length > 1 ? 'sm:grid-cols-2 print:grid-cols-2' : ''}`}>
      {p.map((x, i) => (
        <li key={x.titel} className="flex min-w-0 flex-col gap-1.5 rounded-xl bg-[#f4f5f2] px-4 py-3.5 break-inside-avoid">
          <span className="flex items-center gap-2 text-[0.9375rem] font-semibold text-[#1d1f26]">
            {i === 0 ? (
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="#1f6964" strokeWidth="1.6" strokeLinecap="round" aria-hidden><path d="M3 4h12M3 8h12M3 12h8" /></svg>
            ) : (
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="#1f6964" strokeWidth="1.6" strokeLinejoin="round" aria-hidden><rect x="1.5" y="3" width="11" height="8" rx="1.2" /><rect x="12" y="6" width="4.5" height="9" rx="1" /></svg>
            )}
            {p.length > 1 ? `${i + 1}. ${x.titel}` : x.titel}
          </span>
          <span className="text-[0.875rem] leading-normal text-[#565b62]">{x.satz}</span>
        </li>
      ))}
    </ol>
  );
}

/**
 * Schritt „Text-Simulation: das Ergebnis“: je Fassung eine Zeile mit Wortstufe und wie
 * viele sie vorziehen, die beste zuerst und mit Etikett (nur bei mehr als
 * einer Fassung), die Warnung zur erweiterten Zielgruppe darunter.
 */
function ErgebnisListe({ karten }: { karten: FassungsKarte[] }) {
  const mehrere = karten.length > 1;
  return (
    <ul className="m-0 flex list-none flex-col border-t border-[#e8eceb] p-0">
      {karten.map((k) => {
        const beste = mehrere && k.beste && !!k.stufe;
        return (
          <li key={k.nr} className={`flex flex-col gap-2 border-b border-[#e8eceb] px-3 py-3.5 break-inside-avoid ${beste ? 'bg-[#eef4f3]' : ''}`}>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
              <span className="flex min-w-0 flex-[1_1_220px] flex-col gap-1">
                {beste && <span className="text-[0.75rem] font-semibold tracking-[0.04em] text-[#1f6964] uppercase">{t('Beste Fassung im Text', 'Best version in the text')}</span>}
                <a href={`#${k.anker}`} className="text-[0.96875rem] leading-snug font-semibold text-[#1d1f26] [overflow-wrap:anywhere] underline-offset-[3px] hover:text-[#1f6964] hover:underline">{k.name}</a>
              </span>
              {k.stufe ? (
                <span className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
                  <Skala stufe={k.stufe.stufe} />
                  <span className="min-w-[7rem] text-[0.90625rem] font-semibold text-[#1d1f26]">{k.stufe.wort}</span>
                  <span className="text-[0.84375rem] leading-normal text-[#565b62] sm:w-[19rem]">{k.stufe.satz}</span>
                </span>
              ) : k.weiter ? (
                <span className="text-[0.875rem] leading-normal text-[#1d1f26]">{k.weiter}</span>
              ) : null}
            </div>
            {k.warnung && <span className="rounded-md bg-[#eef2f1] px-3 py-2 text-[0.8125rem] leading-normal text-[#2f3238]">{k.warnung}</span>}
          </li>
        );
      })}
    </ul>
  );
}

/**
 * Im Gestaltungs-Schritt (heute und Skizze): die Noten je Gerät und, wenn es
 * sie gibt, die ersten Bildschirme am Handy, zum Aufklappen an Ort und Stelle.
 * Bis 13.10.2026 sprang hier ein Link ans Ende zum Akkordeon, ein Leser fand
 * das unsanft. Im Druck ausgeblendet: dort steht das Akkordeon offen.
 */
function NotenHierBlock({ g }: { g: Extract<DesignSchritt, { art: 'paar' }> }) {
  const handy = [
    { wer: t('Heute', 'Today'), src: g.bilder.heute?.mobil ?? null },
    { wer: g.name ? t(`Mit „${g.name}“`, `With “${g.name}”`) : t('Mit der neuen Fassung', 'With the new version'), src: g.bilder.neu?.mobil ?? null },
  ].filter((b) => b.src);
  return (
    <details className="group mt-0.5 rounded-[10px] border border-[#dde3e1] print:hidden">
      <summary className="flex min-h-11 cursor-pointer list-none items-center gap-2.5 px-3.5 py-1 text-[0.90625rem] font-semibold text-[#1f6964] [&::-webkit-details-marker]:hidden">
        <svg className="shrink-0 transition-transform duration-200 group-open:rotate-90 motion-reduce:transition-none" width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="#1f6964" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden><path d="M6 3l5 5-5 5" /></svg>
        {handy.length ? t('Noten und Bilder hier aufklappen', 'Show scores and images here') : t('Noten hier aufklappen', 'Show scores here')}
      </summary>
      <div className="flex flex-col gap-3.5 px-3.5 pt-1 pb-4">
        <div className="overflow-x-auto" tabIndex={0} role="region" aria-label={t('Noten der Gestaltung, seitlich scrollbar', 'Design scores, scrolls sideways')}>
          <table className="w-full max-w-[420px] border-collapse text-left text-[0.875rem] text-[#2f3238] tabular-nums">
            <thead>
              <tr className="text-[#5f646b]">
                <th className="py-1.5 pr-2 font-semibold">{t('Gerät', 'Device')}</th>
                <th className="px-2 py-1.5 text-right font-semibold">{t('heute', 'today')}</th>
                <th className="py-1.5 pl-2 text-right font-semibold">{t('neu', 'new')}</th>
              </tr>
            </thead>
            <tbody>
              {g.stufen.map((s) => (
                <tr key={s.geraet} className="border-t border-[#e8eceb]">
                  <td className="py-1.5 pr-2">{s.geraet === 'Desktop' ? t('Am Desktop, Mittel', 'On desktop, average') : t('Am Handy', 'On mobile')}</td>
                  <td className="px-2 py-1.5 text-right">{zahl(s.heute)}</td>
                  <td className="py-1.5 pl-2 text-right">{zahl(s.neu)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {handy.length > 0 && (
          <div className="flex flex-wrap gap-3">
            {handy.map((b) => (
              <figure key={b.wer} className="m-0 flex w-[150px] max-w-[46%] flex-col gap-1.5">
                <a href={b.src!} target="_blank" rel="noopener noreferrer" className="block overflow-hidden rounded-lg border border-[#dde3e1] bg-white">
                  <img src={b.src!} alt={t(`Erster Bildschirm am Handy: ${b.wer}`, `First screen on mobile: ${b.wer}`)} className="block h-[280px] w-full object-cover object-top" />
                  <span className="sr-only">{t(' (ganze Aufnahme in neuem Tab öffnen)', ' (open full capture in a new tab)')}</span>
                </a>
                <figcaption className="text-[0.75rem] leading-snug text-[#5f646b]">{b.wer}</figcaption>
              </figure>
            ))}
          </div>
        )}
        <a href="#akkordeon-design" className="self-start text-[0.84375rem] font-semibold text-[#1f6964] underline-offset-[3px] hover:underline">{t('Alle Kriterien und Befunde des Fachprüfers', 'All criteria and findings of the expert reviewer')}</a>
      </div>
    </details>
  );
}

const gross = (s: string) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

/**
 * Der Aufklapper „Wie die Stufen entstehen“: die Regel, je Fassung die Noten
 * und wer sie vorzieht, die erweiterte Zielgruppe, die Gestaltung und der
 * Umfang. Hier stehen die Zahlen, die oben bis zum 12.10.2026 in den
 * Kennzahl-Karten standen.
 */
function StufenHerleitung({ data, karten, gestaltung, umfang }: { data: any; karten: FassungsKarte[]; gestaltung: DesignSchritt | null; umfang: string }) {
  const varianten: any[] = Array.isArray(data.variants) ? data.variants : [];
  const mitKern = karten.some((k) => k.stufe?.basis === 'kern');
  const mitSchritt = varianten.some((v) => schrittJa(v));
  const erweitert = varianten.map((v, i) => ({ k: karten[i]!, kr: v?.mode === 'feedback' ? null : leseKernRand(v) })).filter((x) => x.kr);
  const vergleich = vergleichImSatz(data);
  const prozent = (n: number | null | undefined) => (n == null ? '' : t(`${n} %`, `${n}%`));
  return (
    <div className="flex max-w-[70ch] flex-col gap-3 pb-5 text-[0.9375rem] leading-relaxed text-[#2f3238] sm:pl-7">
      <p className="m-0">
        {t(<>
          {karten.length > 1 ? `Jede Fassung lief in einem eigenen Vergleich gegen ${vergleich}. ` : ''}Jede Persona liest {vergleich} und die Fassung, in zufälliger Reihenfolge, gibt beiden eine Note von 0 bis 10 und sagt, welche sie vorzieht.
          {mitKern ? ' Gezählt wird die Kernzielgruppe.' : ' Gezählt werden alle Personas.'}
        </>, <>
          {karten.length > 1 ? `Each version ran in its own comparison against ${vergleich}. ` : ''}Each persona reads {vergleich} and the version, in random order, gives both a score from 0 to 10 and says which it prefers.
          {mitKern ? ' The core audience is counted.' : ' All personas are counted.'}
        </>)}
      </p>
      <p className="m-0">
        {t(<>
          Gleichauf: {mitKern ? 'die Kernzielgruppe' : 'die Wertung'} teilt sich, oder die Noten liegen weniger als 0,3 auseinander.
          Etwas: unter 1 Punkt Abstand. Klar: ab 1 Punkt. Zeigen Noten und Mehrheit in verschiedene Richtungen, gilt die Richtung der Mehrheit.
        </>, <>
          Level: {mitKern ? 'the core audience' : 'the vote'} is split, or the scores are less than 0.3 apart.
          Slightly: a gap under 1 point. Clearly: 1 point or more. If scores and majority point in different directions, the direction of the majority applies.
        </>)}
      </p>
      <div className="overflow-x-auto" tabIndex={0} role="region" aria-label={t('Noten je Fassung, seitlich scrollbar', 'Scores by version, scrolls sideways')}>
        <table className="w-full border-collapse text-left text-[0.8125rem] text-[#2f3238]">
          <thead>
            <tr className="border-b border-[#cbd3d1] text-[#5f646b]">
              <th className="py-1.5 pr-3 font-semibold">{t('Fassung', 'Version')}</th>
              <th className="py-1.5 pr-3 font-semibold whitespace-nowrap">{t('Note Vergleich', 'Baseline score')}</th>
              <th className="py-1.5 pr-3 font-semibold whitespace-nowrap">{t('Note Fassung', 'Version score')}</th>
              <th className="py-1.5 pr-3 font-semibold whitespace-nowrap">{mitKern ? t('Kern zieht sie vor', 'Core prefers it') : t('Ziehen sie vor', 'Prefer it')}</th>
              {mitSchritt && <th className="py-1.5 pr-3 font-semibold whitespace-nowrap">{t('Würden weitergehen', 'Would go on')}</th>}
              <th className="py-1.5 font-semibold">{t('Stufe', 'Level')}</th>
            </tr>
          </thead>
          <tbody>
            {karten.map((k) => {
              const s = k.stufe;
              const ja = schrittJa(varianten[k.nr - 1]);
              return (
                <tr key={k.nr} className="border-b border-[#e8eceb] align-top">
                  <td className="py-1.5 pr-3 [overflow-wrap:anywhere]">{k.name}</td>
                  <td className="py-1.5 pr-3 whitespace-nowrap tabular-nums">{s?.vergleich != null ? zahl(s.vergleich) : ''}</td>
                  <td className="py-1.5 pr-3 whitespace-nowrap tabular-nums">{s?.fassung != null ? zahl(s.fassung) : ''}</td>
                  <td className="py-1.5 pr-3 whitespace-nowrap tabular-nums">{s ? t(`${s.dafuer} von ${s.von}${s.urteile ? ' Urteilen' : ''}`, `${s.dafuer} of ${s.von}${s.urteile ? ' verdicts' : ''}`) : k.weiter}</td>
                  {mitSchritt && <td className="py-1.5 pr-3 whitespace-nowrap tabular-nums">{ja ? (ja.vergleich != null ? `${prozent(ja.fassung)} ${t('zu', 'to')} ${prozent(ja.vergleich)}` : prozent(ja.fassung)) : ''}</td>}
                  <td className="py-1.5 whitespace-nowrap">{s?.wort ?? ''}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {mitSchritt && <p className="m-0 text-[0.8125rem] text-[#5f646b]">{t(
        <>„Würden weitergehen“: Anteil, der den nächsten Schritt gehen würde, mit dieser Fassung zu mit der Vergleichsfassung{mitKern ? ', in der Kernzielgruppe' : ''}. Aussagekräftig ist der Unterschied zwischen den Fassungen, nicht die Höhe: KI-Personas urteilen in Studien wohlwollender als echte Leser, eine erwartete Quote für den Markt ist das nicht.</>,
        <>“Would go on”: share who would take the next step, with this version versus with the baseline{mitKern ? ', in the core audience' : ''}. What carries meaning is the difference between the versions, not the level: in studies, AI personas judge more favorably than real readers, so this is not an expected rate for the market.</>,
      )}</p>}
      {erweitert.length > 0 && (
        <p className="m-0">
          {t('Die erweiterte Zielgruppe zählt nicht ins Urteil. Ihre Stimmen:', 'The extended audience does not count toward the verdict. Its votes:')}{' '}
          {erweitert.map(({ k, kr }, i) => t(
            <span key={k.nr}>{i > 0 && '; '}{k.name} <span className="whitespace-nowrap">{kr!.rand.wins.candidate} von {kr!.rand.n}</span> dafür, Noten <span className="whitespace-nowrap">{zahl(kr!.rand.scores.candidate)} zu {zahl(kr!.rand.scores.baseline)}</span></span>,
            <span key={k.nr}>{i > 0 && '; '}{k.name} <span className="whitespace-nowrap">{kr!.rand.wins.candidate} of {kr!.rand.n}</span> in favor, scores <span className="whitespace-nowrap">{zahl(kr!.rand.scores.candidate)} to {zahl(kr!.rand.scores.baseline)}</span></span>,
          ))}.
        </p>
      )}
      {gestaltung?.art === 'paar' && (
        <p className="m-0">
          {t('Gestaltung: Desktop ist das Mittel aus erstem Eindruck, „wirkt für Leute wie mich gemacht“, seriös und Orientierung, Handy die Note am Handy.',
            'Design: desktop is the average of first impression, “looks made for people like me”, trustworthy and orientation; mobile is the mobile score.')}{' '}
          {gestaltung.stufen.map((g, i) => <span key={g.geraet}>{i > 0 && ', '}{geraetName(g.geraet)} <span className="whitespace-nowrap">{zahl(g.neu)} {t('zu', 'to')} {zahl(g.heute)}</span></span>)}{t(', jeweils mit der neuen Fassung zu heute.', ', each with the new version versus today.')}{' '}
          {t('Dieselben Stufen, ohne Mehrheit: heute und neu sind zwei getrennte Design-Checks.', 'The same levels, without a majority: today and new are two separate design checks.')}
        </p>
      )}
      {umfang && <p className="m-0 text-[0.875rem] text-[#5f646b]">{t('Grundlage', 'Basis')}: {umfang}.</p>}
    </div>
  );
}

/**
 * Ein Abschnitt zum Aufklappen, als echter Knopf mit aria-expanded. Der Inhalt
 * bleibt im DOM, damit Sprünge (#fassung-n, #empfehlung-n) ihr Ziel finden;
 * im Druck steht er immer offen.
 */
function Akkordeon({ id, titel, kurz, offen, umschalten, children }: {
  id: string; titel: string; kurz?: string; offen: boolean; umschalten: () => void; children: React.ReactNode;
}) {
  return (
    <div id={`akkordeon-${id}`} data-akkordeon={id} className="scroll-mt-6 border-t border-[#dde3e1]">
      <h3 className="m-0">
        <button type="button" aria-expanded={offen} aria-controls={`akkordeon-inhalt-${id}`} onClick={umschalten}
          className="flex min-h-14 w-full cursor-pointer items-center justify-between gap-4 bg-transparent px-0 py-3.5 text-left text-[0.9375rem] font-semibold text-[#1d1f26] hover:bg-[#f2f5f4] focus-visible:outline-3 focus-visible:outline-offset-2 focus-visible:outline-[#1f6964] sm:px-5 sm:text-base">
          <span className="min-w-0">{titel}{kurz && <span className="font-normal text-[#5f646b]"> · {kurz}</span>}</span>
          <span aria-hidden className="shrink-0 text-[1.125rem] leading-none print:hidden">{offen ? '−' : '+'}</span>
        </button>
      </h3>
      <div id={`akkordeon-inhalt-${id}`} className={`${offen ? 'block' : 'hidden'} pt-1 pb-8 sm:px-5 print:block`}>{children}</div>
    </div>
  );
}

const ART_FARBE: Record<Art, string> = { wirkt: '#5e8a62', bremst: '#d07a3e', fehlt: '#7d8487' };
const ART_EN: Record<Art, string> = { wirkt: 'works', bremst: 'holds back', fehlt: 'missing' };
/** wirkt, bremst, fehlt als Wort der Seite; Unbekanntes bleibt, wie es kommt. */
const artWort = (a: string) => (sprache() === 'en' ? ART_EN[a as Art] ?? a : a);

/**
 * Befunde je Abschnitt. Am Desktop jede Zeile offen; am Handy eine Liste zum
 * Aufklappen, der erste Abschnitt mit Handlungsbedarf steht offen.
 */
function AbschnittsListe({ zeilen, kurz, ebene = 3 }: { zeilen: AbschnittsZeile[]; kurz: (label: string) => string; ebene?: 3 | 4 }) {
  const [offen, setOffen] = useState<Set<number>>(() => {
    const erster = zeilen.find((z) => z.status === 'fix');
    return new Set(erster ? [erster.nr] : []);
  });
  return (
    <div className="flex flex-col">
      {zeilen.map((z) => (
        <AbschnittsReihe key={z.nr} z={z} kurz={kurz} ebene={ebene} offen={offen.has(z.nr)}
          umschalten={() => setOffen((o) => { const x = new Set(o); if (x.has(z.nr)) x.delete(z.nr); else x.add(z.nr); return x; })} />
      ))}
    </div>
  );
}

function AbschnittsReihe({ z, kurz, offen, umschalten, ebene = 3 }: {
  z: AbschnittsZeile; kurz: (label: string) => string; offen: boolean; umschalten: () => void; ebene?: 3 | 4;
}) {
  const etikett = z.status === 'ok' ? 'bg-[#e4ede3] text-[#2f5a34]' : 'bg-[#f6e3d2] text-[#8a4a17]';
  // Im Akkordeon (S3) eine Ebene unter dessen Titel, sonst unter „Befunde je Abschnitt“ (h2).
  const H = ebene === 4 ? 'h4' : 'h3';
  return (
    <article className="border-b border-[#dde3e1] break-inside-avoid">
      <H className="m-0 md:hidden print:hidden">
        <button type="button" aria-expanded={offen} aria-controls={`abschnitt-${z.nr}`} onClick={umschalten}
          className="flex min-h-[52px] w-full cursor-pointer items-center justify-between gap-3 bg-transparent px-0 py-3 text-left text-[0.9375rem] font-semibold text-[#1d1f26] focus-visible:outline-3 focus-visible:outline-offset-2 focus-visible:outline-[#1f6964]">
          <span className="flex min-w-0 items-center gap-2">
            <span aria-hidden className={`size-[9px] shrink-0 rounded-full ${z.status === 'ok' ? 'bg-[#5e8a62]' : 'bg-[#3f8a87]'}`} />
            <span className="min-w-0 [overflow-wrap:anywhere]">{z.nr}. {z.titel}</span>
            <span className="sr-only">, {z.etikett}</span>
          </span>
          <span aria-hidden className="shrink-0 text-[1.125rem] leading-none">{offen ? '−' : '+'}</span>
        </button>
      </H>
      <div id={`abschnitt-${z.nr}`}
        className={`${offen ? 'grid' : 'hidden'} gap-x-7 gap-y-4 pb-5 md:grid md:grid-cols-[minmax(0,1fr)_17rem] md:py-[22px] lg:grid-cols-[minmax(0,1fr)_18.75rem] print:grid print:grid-cols-[minmax(0,1fr)_13rem] print:py-4`}>
        <div className="flex min-w-0 flex-col gap-2.5">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
            <H className="m-0 hidden text-[1.1875rem] leading-snug font-semibold text-[#1d1f26] md:block print:block">{z.nr}. {z.titel}</H>
            <span className={`rounded-full px-2.5 py-1 text-[0.75rem] leading-none font-semibold ${etikett}`}>{z.etikett}</span>
          </div>
          {z.befund && <p className="m-0 max-w-[68ch] text-[0.9375rem] leading-relaxed text-[#2f3238]">{z.befund}</p>}
          {z.zitate.map((q, i) => (
            <blockquote key={i} className="m-0 mt-1 max-w-[68ch] text-[1.0625rem] leading-snug text-[#2f3238] italic font-serif">
              {z.zitate.length > 1 && <span className="mr-1.5 text-[0.6875rem] font-semibold tracking-[0.08em] text-[#5f646b] not-italic uppercase font-sans">{artWort(q.art)}</span>}
              {zitiert(q.text)}{' '}
              <span className="text-[0.78125rem] text-[#5f646b] not-italic font-sans">{kurz(q.persona)}</span>
            </blockquote>
          ))}
          {z.empfehlungen.filter((e) => e.vorschlag).map((e) => (
            <p key={e.nr} className="m-0 mt-1 max-w-[68ch] rounded-[10px] bg-[#eef2f1] px-3.5 py-3 text-[0.90625rem] leading-relaxed text-[#1d1f26]">
              <b>{t('Vorschlag:', 'Suggestion:')}</b> {e.vorschlag}{' '}
              <a href={`#empfehlung-${e.nr}`} className="whitespace-nowrap text-[0.8125rem] text-[#1f6964] underline-offset-2 hover:underline print:hidden">{t('Empfehlung', 'Recommendation')} {e.nr}</a>
            </p>
          ))}
        </div>
        <div className="flex flex-col gap-2.5 text-[0.8125rem]" aria-label={t(`Nennungen: wirkt ${z.zahlen.wirkt.n}, bremst ${z.zahlen.bremst.n}, fehlt ${z.zahlen.fehlt.n}, von ${z.zahlen.wirkt.von}`, `Mentions: works ${z.zahlen.wirkt.n}, holds back ${z.zahlen.bremst.n}, missing ${z.zahlen.fehlt.n}, of ${z.zahlen.wirkt.von}`)}>
          {(['wirkt', 'bremst', 'fehlt'] as const).map((art) => {
            const { n, von } = z.zahlen[art];
            return (
              <div key={art} className="flex items-center gap-2.5" aria-hidden>
                <span className={`${sprache() === 'en' ? 'w-[76px]' : 'w-[52px]'} shrink-0 text-[#5f646b]`}>{artWort(art)}</span>
                <span className="h-2 min-w-0 flex-1 rounded-[5px] bg-[#e8eceb]">
                  {n > 0 && <span className="block h-full rounded-[5px]" style={{ width: `${von ? Math.min(100, (n / von) * 100) : 0}%`, background: ART_FARBE[art] }} />}
                </span>
                <b className="w-12 shrink-0 text-right font-semibold tabular-nums">{n} / {von}</b>
              </div>
            );
          })}
        </div>
      </div>
    </article>
  );
}

/** Die Empfehlungen des Autors, nummeriert, mit Vorschlag und wen sie abholen. */
function EmpfehlungsListe({ empfehlungen, vorschlagEtikett = t('Vorschlag · vor dem Livegang prüfen', 'Suggestion · check before launch') }: { empfehlungen: any[]; vorschlagEtikett?: string }) {
  return (
    <ol className="m-0 list-none overflow-hidden rounded-xl border border-[#dde3e1] bg-[#ffffff] p-0">
      {empfehlungen.map((e: any, i: number) => (
        <li key={i} id={`empfehlung-${i + 1}`} className={`grid scroll-mt-6 gap-x-5 gap-y-3 border-t border-[#e8eceb] px-5 py-5 first:border-t-0 break-inside-avoid grid-cols-[2.5rem_minmax(0,1fr)] ${Array.isArray(e.holt_ab) && e.holt_ab.length > 0 ? 'sm:grid-cols-[2.5rem_minmax(0,1fr)_13rem]' : ''}`}>
          <span className={`${KOPF} flex size-10 items-center justify-center rounded-full bg-[#1d1f26] text-[1.125rem] text-[#f4f5f2]`}>{i + 1}</span>
          <div className="min-w-0 max-w-[72ch]">
            <h3 className="mt-1 mb-1.5 text-[1.0625rem] font-semibold text-[#1d1f26]">{e.titel}</h3>
            {e.text && <p className="m-0 leading-relaxed text-[#2f3238]">{e.text}</p>}
            {e.vorschlag && (
              <div className="mt-3 rounded-lg border border-dashed border-[#b7c0be] bg-[#f9fbfa] px-3.5 py-2.5">
                <span className="block font-mono text-[0.625rem] font-bold tracking-[0.14em] text-[#5f646b] uppercase">{vorschlagEtikett}</span>
                <span className="text-[#1d1f26]">{e.vorschlag}</span>
              </div>
            )}
            {e.kollidiert_mit && <KollisionsHinweis leitplanke={String(e.kollidiert_mit)} />}
          </div>
          {Array.isArray(e.holt_ab) && e.holt_ab.length > 0 && (
            <div className="col-span-2 min-w-0 sm:col-span-1">
              <span className="block font-mono text-[0.625rem] font-bold tracking-[0.14em] text-[#5f646b] uppercase">{t('Holt ab', 'Wins over')}</span>
              <ul className="m-0 mt-1.5 flex list-none flex-wrap gap-1.5 p-0">
                {e.holt_ab.map((h: string) => <li key={h} className="rounded bg-[#dcefed] px-2 py-0.5 text-[0.75rem] text-[#163f3c]">{h}</li>)}
              </ul>
            </div>
          )}
        </li>
      ))}
    </ol>
  );
}
